import { useEffect, useMemo, useState } from "react";
import { TaskDisclosure } from "../../components/TaskDisclosure";

const RESOURCE_SECTION_IDS = ["inventory", "reservations", "operating-rules", "status-maintenance"];

function normalizeResourceSection(value) {
  if (RESOURCE_SECTION_IDS.includes(value)) {
    return value;
  }
  if (value === "access-restrictions") {
    return "operating-rules";
  }
  if (value === "availability-check") {
    return "reservations";
  }
  if (value === "blocks") {
    return "status-maintenance";
  }
  return "inventory";
}

function statusLabel(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function resourceTypeLabel(value) {
  const labels = {
    area: "Zone",
    bay: "Range",
    classroom: "Classroom",
    lane: "Lane",
    target_pit: "Target pit"
  };
  return labels[value] || statusLabel(value);
}

function resourceRuleTypeLabel(value) {
  const labels = {
    area: "Zone",
    bay: "Range",
    classroom: "Classroom",
    lane: "Lane",
    target_pit: "Target pit"
  };
  return labels[value] || statusLabel(value);
}

function buildResourceMap(resources = []) {
  return new Map(resources.map((resource) => [resource.resourceId, resource]));
}

function buildChildrenByParentId(resources = []) {
  return resources.reduce((accumulator, resource) => {
    if (!resource.parentResourceId) {
      return accumulator;
    }
    const children = accumulator.get(resource.parentResourceId) || [];
    children.push(resource);
    accumulator.set(resource.parentResourceId, children);
    return accumulator;
  }, new Map());
}

function resourceDepth(resource, resourceById = new Map()) {
  let depth = 0;
  let current = resource;
  const visited = new Set();
  while (
    current?.parentResourceId &&
    resourceById.has(current.parentResourceId) &&
    !visited.has(current.resourceId)
  ) {
    visited.add(current.resourceId);
    current = resourceById.get(current.parentResourceId);
    depth += 1;
  }
  return Math.min(depth, 2);
}

function resourceHierarchyLevel(resource, resourceById = new Map()) {
  if (resource?.type === "lane") {
    return "lane";
  }
  if (!resource?.parentResourceId) {
    return /zone/i.test(resource?.name || "") || resource?.attributes?.hierarchyLevel === "zone" ? "zone" : "range";
  }
  const parent = resourceById.get(resource.parentResourceId);
  if (parent && resourceHierarchyLevel(parent, resourceById) === "zone") {
    return "range";
  }
  if (parent && resourceHierarchyLevel(parent, resourceById) === "range") {
    return "lane";
  }
  const depth = resourceDepth(resource, resourceById);
  if (depth === 0) {
    return "zone";
  }
  if (depth === 1) {
    return "range";
  }
  return "lane";
}

function hierarchyLevelLabel(level) {
  const labels = {
    zone: "Zone",
    range: "Range",
    lane: "Lane"
  };
  return labels[level] || "Facility";
}

function resourceHierarchyLabel(resource, resourceById = new Map()) {
  return hierarchyLevelLabel(resourceHierarchyLevel(resource, resourceById));
}

function childHierarchyLabel(parentResource, resourceById = new Map()) {
  const parentLevel = resourceHierarchyLevel(parentResource, resourceById);
  if (parentLevel === "zone") {
    return "range";
  }
  return "lane";
}

function pluralizeHierarchyLevel(level, count) {
  const label = hierarchyLevelLabel(level).toLowerCase();
  return `${count} ${label}${count === 1 ? "" : "s"}`;
}

function collectDescendantResources(resource, childrenByParentId = new Map()) {
  const descendants = [];
  const collect = (current) => {
    (childrenByParentId.get(current.resourceId) || []).forEach((child) => {
      descendants.push(child);
      collect(child);
    });
  };
  if (resource) {
    collect(resource);
  }
  return descendants;
}

function resourcePathLabel(resource, resourceById = new Map()) {
  const path = [];
  let current = resource;
  const visited = new Set();
  while (current && !visited.has(current.resourceId)) {
    path.unshift(current.name);
    visited.add(current.resourceId);
    current = current.parentResourceId ? resourceById.get(current.parentResourceId) : null;
  }
  return path.join(" / ");
}

function resourceTypeForHierarchyLevel(level, currentType = "bay") {
  if (level === "zone") {
    return currentType === "classroom" || currentType === "target_pit" ? currentType : "area";
  }
  if (level === "lane") {
    return "lane";
  }
  return currentType === "classroom" || currentType === "target_pit" ? currentType : "bay";
}

function isArmoryResource(resource = {}) {
  const attributes = resource.attributes || {};
  return String(attributes.assetClass || "").toLowerCase() === "firearm" ||
    Boolean(attributes.firearmMake || attributes.firearmModel || attributes.caliber);
}

function publicationStatusLabel(value) {
  const labels = {
    open: "Open for members",
    members_only: "Members only",
    limited_preview: "Limited preview",
    under_construction: "Under construction",
    seasonal_closed: "Seasonally closed"
  };
  return labels[value] || labels.open;
}

function formatDistance(resource) {
  const distance = resource.distance || {};
  const unit = distance.unit || "yards";
  if (Array.isArray(distance.fixed) && distance.fixed.length > 0) {
    return `${distance.fixed.join(", ")} ${unit}`;
  }
  if (distance.max) {
    return `${distance.min || 0}-${distance.max} ${unit}`;
  }
  return "Distance not set";
}

function hasResourceDistance(resource) {
  const distance = resource.distance || {};
  return Boolean((Array.isArray(distance.fixed) && distance.fixed.length > 0) || distance.max || distance.min);
}

function formatResourceCapacity(resource, childResources = [], childUnit = "lane") {
  if (childResources.length > 0) {
    const childShooterCapacity = childResources.reduce((total, child) => (
      total + Number(child.capacity?.shooters || 0)
    ), 0);
    const shooterText = childShooterCapacity > 0
      ? ` / ${childShooterCapacity} shooter${childShooterCapacity === 1 ? "" : "s"}`
      : "";
    return `${childResources.length} ${childUnit}${childResources.length === 1 ? "" : "s"}${shooterText}`;
  }

  const shooters = Number(resource.capacity?.shooters || 0);
  const lanes = Number(resource.capacity?.lanes || 0);
  const parts = [];
  if (shooters > 0) {
    parts.push(`${shooters} shooter${shooters === 1 ? "" : "s"}`);
  }
  if (resource.type !== "lane" && lanes > 0) {
    parts.push(`${lanes} lane${lanes === 1 ? "" : "s"}`);
  }
  return parts.length > 0 ? `${parts.join(" / ")} capacity` : "";
}

function resourceMatchesFilters(resource, filters = {}, resourceById = new Map()) {
  const query = String(filters.q || "").trim().toLowerCase();
  const hierarchyLevel = resourceHierarchyLevel(resource, resourceById);
  const haystack = [
    resource.name,
    resource.type,
    hierarchyLevel,
    resource.status,
    resource.targetControl?.status,
    resource.notes,
    resource.description,
    resource.availabilityPolicy?.publicationStatus,
    resource.availabilityPolicy?.publicationNote,
    resource.availabilityPolicy?.notes,
    resource.attributes?.maintenancePlanName,
    resource.attributes?.maintenanceChecklist,
    resource.attributes?.maintenanceAssignedTo,
    ...(resource.accessPolicy?.requiredQualificationKeys || [])
  ].join(" ").toLowerCase();
  return (
    (!filters.type || resource.type === filters.type || hierarchyLevel === filters.type) &&
    (!filters.status || (resource.status || "open") === filters.status) &&
    (!filters.targetControlStatus || (resource.targetControl?.status || "firing_allowed") === filters.targetControlStatus) &&
    (!filters.reservable || String(Boolean(resource.reservable)) === filters.reservable) &&
    (!query || haystack.includes(query))
  );
}

function getResourceNotes(resource = {}) {
  return resource.attributes?.notes || resource.notes || resource.description || "";
}

function resourceFiltersActive(filters = {}) {
  return Boolean(filters.type || filters.status || filters.targetControlStatus || filters.reservable || filters.q);
}

function isResourceBookable(resource) {
  return Boolean(resource.reservable && resource.reservationRules?.enabled);
}

function formatReservationRules(resource) {
  const rules = resource.reservationRules || {};
  if (!isResourceBookable(resource)) {
    return "Not reservable";
  }
  const cutoff = Number(rules.cancellationCutoffHours || 0) > 0
    ? ` / ${rules.cancellationCutoffHours}h change cutoff`
    : "";
  return `${rules.minDurationMinutes}-${rules.maxDurationMinutes} min / ${rules.slotSizeMinutes} min slots${cutoff}`;
}

function formatQualificationLabel(key, qualificationCatalog = []) {
  const match = qualificationCatalog.find((qualification) => qualification.key === key);
  return match?.label || key;
}

function formatRequiredQualifications(resource, qualificationCatalog = []) {
  const keys = resource.accessPolicy?.requiredQualificationKeys || [];
  return keys.length > 0 ? keys.map((key) => formatQualificationLabel(key, qualificationCatalog)).join(", ") : "No qualification gate";
}

function formatResourceAvailabilityPolicy(resource) {
  const policy = resource.availabilityPolicy || {};
  const parts = [publicationStatusLabel(policy.publicationStatus || "open")];
  if (policy.inheritRangeHours === false) {
    parts.push("Own schedule");
  } else {
    parts.push("Uses range hours");
  }
  if (policy.startsOn || policy.endsOn) {
    parts.push(`${policy.startsOn || "Any date"} to ${policy.endsOn || "No end"}`);
  }
  if (policy.openTime && policy.closeTime) {
    parts.push(`${policy.openTime}-${policy.closeTime}`);
  }
  return parts.join(" / ");
}

function formatRuleBookingSummary(resource) {
  const rules = resource.reservationRules || {};
  if (!isResourceBookable(resource)) {
    return "Member booking off";
  }
  const min = Number(rules.minDurationMinutes || 30);
  const max = Number(rules.maxDurationMinutes || 120);
  const slot = Number(rules.slotSizeMinutes || 30);
  const cutoff = Number(rules.cancellationCutoffHours || 0) > 0
    ? ` / ${rules.cancellationCutoffHours}h change cutoff`
    : "";
  return `${min}-${max} min sessions / ${slot} min slots${cutoff}`;
}

function formatRuleAvailabilitySummary(resource) {
  const policy = resource.availabilityPolicy || {};
  const publication = publicationStatusLabel(policy.publicationStatus || "open");
  const schedule = policy.inheritRangeHours === false ? "Own schedule" : "Range hours";
  const dateWindow = policy.startsOn || policy.endsOn
    ? ` / ${policy.startsOn || "Any start"} to ${policy.endsOn || "No end"}`
    : "";
  const timeWindow = policy.openTime && policy.closeTime ? ` / ${policy.openTime}-${policy.closeTime}` : "";
  return `${publication} / ${schedule}${dateWindow}${timeWindow}`;
}

function formatRuleCeaseFireSummary(resource) {
  const cycle = resource.targetControl?.cycle || {};
  if (!cycle.enabled) {
    return "No repeating cycle";
  }
  const startsAt = cycle.startsAt ? ` / starts ${formatCycleStart(cycle.startsAt)}` : "";
  return `${cycle.firingMinutes || 0} min hot / ${cycle.targetCheckMinutes || 0} min cease-fire${startsAt}`;
}

function getStatusBoardControlState(resource) {
  const status = resource.status || "open";
  const targetStatus = resource.targetControl?.status || "firing_allowed";
  if (status === "maintenance") {
    return { label: "Maintenance lockout", tone: "blocked" };
  }
  if (status === "closed") {
    return { label: "Closed to shooters", tone: "blocked" };
  }
  if (status === "temporarily_reserved") {
    return { label: "Held for owner use", tone: "held" };
  }
  if (targetStatus === "target_check") {
    return { label: "Cease-fire active", tone: "warning" };
  }
  if (targetStatus === "paused") {
    return { label: "Live fire paused", tone: "warning" };
  }
  if (targetStatus === "closed") {
    return { label: "Target line closed", tone: "blocked" };
  }
  return { label: "No cease-fire active", tone: "clear" };
}

function dateOnlyInput(value) {
  if (!value) {
    return "";
  }
  return String(value).slice(0, 10);
}

function inventoryDraftFromResource(resource = {}) {
  const distance = resource.distance || {};
  const attributes = resource.attributes || {};
  return {
    hierarchyLevel: "zone",
    type: resource.type || "bay",
    name: resource.name || "",
    parentResourceId: resource.parentResourceId || "",
    shooterCapacity: String(resource.capacity?.shooters || 0),
    laneCapacity: String(resource.capacity?.lanes || 0),
    distanceMin: String(distance.min || 0),
    distanceMax: String(distance.max || 0),
    fixedDistances: Array.isArray(distance.fixed) ? distance.fixed.join(", ") : "",
    environment: attributes.environment || "outdoor",
    assetClass: attributes.assetClass || "",
    serialNumber: attributes.serialNumber || "",
    rentalStatus: attributes.rentalStatus || "not_rental",
    maintenanceDueAt: dateOnlyInput(attributes.maintenanceDueAt),
    maintenancePlanName: attributes.maintenancePlanName || "",
    maintenanceIntervalDays: String(attributes.maintenanceIntervalDays || ""),
    maintenanceChecklist: attributes.maintenanceChecklist || "",
    maintenanceAssignedTo: attributes.maintenanceAssignedTo || "",
    notes: getResourceNotes(resource)
  };
}

function inventoryPatchFromDraft(draft) {
  const fixedDistances = draft.fixedDistances
    .split(",")
    .map((item) => Number.parseInt(item.trim(), 10))
    .filter((item) => Number.isFinite(item) && item > 0);
  return {
    type: draft.type,
    name: draft.name.trim(),
    parentResourceId: draft.hierarchyLevel === "zone" ? undefined : draft.parentResourceId || undefined,
    capacity: {
      shooters: Number.parseInt(draft.shooterCapacity || "0", 10),
      lanes: Number.parseInt(draft.laneCapacity || "0", 10)
    },
    distance: {
      unit: "yards",
      min: Number.parseInt(draft.distanceMin || "0", 10),
      max: Number.parseInt(draft.distanceMax || "0", 10),
      fixed: fixedDistances
    },
    attributes: {
      environment: draft.environment,
      assetClass: draft.assetClass,
      serialNumber: draft.serialNumber,
      rentalStatus: draft.rentalStatus,
      maintenanceDueAt: draft.maintenanceDueAt,
      maintenancePlanName: draft.maintenancePlanName,
      maintenanceIntervalDays: draft.maintenanceIntervalDays,
      maintenanceChecklist: draft.maintenanceChecklist,
      maintenanceAssignedTo: draft.maintenanceAssignedTo,
      notes: draft.notes
    }
  };
}

function operatingRulesDraftFromResource(resource = {}) {
  const rules = resource.reservationRules || {};
  const payment = rules.payment || {};
  const policy = resource.availabilityPolicy || {};
  const cycle = resource.targetControl?.cycle || {};
  return {
    reservable: Boolean(resource.reservable),
    minDurationMinutes: String(rules.minDurationMinutes || 30),
    maxDurationMinutes: String(rules.maxDurationMinutes || 120),
    slotSizeMinutes: String(rules.slotSizeMinutes || 30),
    bufferMinutes: String(rules.bufferMinutes || 0),
    cancellationCutoffHours: String(rules.cancellationCutoffHours || 0),
    reservationFeeEnabled: Boolean(payment.enabled),
    reservationFeeAmount: String(Number(payment.amountCents || 0) / 100),
    reservationFeeCurrency: payment.currency || "usd",
    reservationFeeLabel: payment.label || "Reservation fee",
    requiredQualificationKeys: (resource.accessPolicy?.requiredQualificationKeys || []).join(", "),
    publicationStatus: policy.publicationStatus || "open",
    publicationNote: policy.publicationNote || "",
    availabilityInheritRangeHours: policy.inheritRangeHours !== false,
    availabilityStartsOn: policy.startsOn || "",
    availabilityEndsOn: policy.endsOn || "",
    availabilityOpenTime: policy.openTime || "",
    availabilityCloseTime: policy.closeTime || "",
    availabilityNotes: policy.notes || "",
    targetCycleEnabled: Boolean(cycle.enabled),
    targetCycleStartsAt: isoToDateTimeLocalInput(cycle.startsAt),
    targetCycleFiringMinutes: String(cycle.firingMinutes || 45),
    targetCycleCheckMinutes: String(cycle.targetCheckMinutes || 15),
    targetCycleNote: cycle.note || ""
  };
}

function operatingRulesPatchFromDraft(draft, resource = {}) {
  return {
    reservable: draft.reservable,
    reservationRules: {
      enabled: draft.reservable,
      minDurationMinutes: Number.parseInt(draft.minDurationMinutes || "30", 10),
      maxDurationMinutes: Number.parseInt(draft.maxDurationMinutes || "120", 10),
      slotSizeMinutes: Number.parseInt(draft.slotSizeMinutes || "30", 10),
      bufferMinutes: Number.parseInt(draft.bufferMinutes || "0", 10),
      cancellationCutoffHours: Number.parseInt(draft.cancellationCutoffHours || "0", 10),
      payment: {
        enabled: draft.reservationFeeEnabled,
        amountCents: Math.round(Number.parseFloat(draft.reservationFeeAmount || "0") * 100),
        currency: draft.reservationFeeCurrency,
        label: draft.reservationFeeLabel
      }
    },
    accessPolicy: {
      requiredQualificationKeys: parseQualificationKeyInput(draft.requiredQualificationKeys)
    },
    availabilityPolicy: {
      publicationStatus: draft.publicationStatus || "open",
      publicationNote: draft.publicationNote || undefined,
      inheritRangeHours: draft.availabilityInheritRangeHours,
      startsOn: draft.availabilityStartsOn || undefined,
      endsOn: draft.availabilityEndsOn || undefined,
      openTime: draft.availabilityOpenTime || undefined,
      closeTime: draft.availabilityCloseTime || undefined,
      notes: draft.availabilityNotes || undefined
    },
    targetControl: {
      status: resource.targetControl?.status || "firing_allowed",
      note: resource.targetControl?.note || "",
      cycle: {
        enabled: draft.targetCycleEnabled,
        firingMinutes: Number.parseInt(draft.targetCycleFiringMinutes || "0", 10),
        targetCheckMinutes: Number.parseInt(draft.targetCycleCheckMinutes || "0", 10),
        startsAt: draft.targetCycleStartsAt || undefined,
        note: draft.targetCycleNote
      }
    }
  };
}

function parseQualificationKeyInput(value = "") {
  return Array.from(new Set(String(value)
    .split(/[\n,]+/)
    .map((item) => item.trim().toLowerCase())
    .map((item) => item.replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, ""))
    .filter(Boolean)));
}

function buildQualificationOptions(qualificationCatalog = [], selectedKeys = []) {
  const normalizedSelectedKeys = Array.from(new Set(selectedKeys.filter(Boolean)));
  const options = qualificationCatalog
    .filter((qualification) => qualification.key && qualification.status !== "inactive")
    .map((qualification) => ({
      key: qualification.key,
      label: qualification.label || qualification.key,
      description: qualification.description || qualification.notes || "",
      catalog: true
    }));
  const optionKeys = new Set(options.map((option) => option.key));
  normalizedSelectedKeys.forEach((key) => {
    if (!optionKeys.has(key)) {
      options.push({
        key,
        label: key,
        description: "Existing gate key not found in the active qualification catalog.",
        catalog: false
      });
    }
  });
  return options;
}

function formatDateTime(value) {
  if (!value) {
    return "Not scheduled";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(date);
}

function formatCycleStart(value) {
  if (!value) {
    return "";
  }
  const raw = String(value).trim();
  if (/^\d{1,2}:\d{2}$/.test(raw)) {
    const [hourValue, minuteValue] = raw.split(":").map((part) => Number.parseInt(part, 10));
    if (Number.isFinite(hourValue) && Number.isFinite(minuteValue)) {
      const date = new Date();
      date.setHours(hourValue, minuteValue, 0, 0);
      return new Intl.DateTimeFormat(undefined, { timeStyle: "short" }).format(date);
    }
  }
  return formatDateTime(raw);
}

function formatBlockWindow(block) {
  return `${formatDateTime(block.startsAt)} - ${formatDateTime(block.endsAt)}`;
}

function formatTimeRange(item) {
  const formatter = new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit"
  });
  return `${formatter.format(new Date(item.startsAt))} - ${formatter.format(new Date(item.endsAt))}`;
}

function formatAvailabilityReason(item) {
  const labels = {
    availability_block_overlap: "Availability block conflict",
    duration_too_long: "Duration too long",
    duration_too_short: "Duration too short",
    not_reservable: "Not reservable",
    outside_business_hours: "Outside business hours",
    outside_resource_availability: "Outside facility hours",
    reservation_overlap: "Reservation conflict",
    reservation_rules_disabled: "Reservation rules disabled",
    resource_not_open: "Facility not open",
    slot_size_mismatch: "Slot size mismatch",
    target_control_not_clear: "Target control not clear"
  };
  const fallbacks = {
    outside_business_hours: "Choose a time inside the range operating schedule.",
    outside_resource_availability: "Choose a date and time inside this facility's configured window.",
    reservation_overlap: "Choose another facility or time window.",
    target_control_not_clear: "Wait until firing is allowed before reserving this facility."
  };
  return {
    title: labels[item.code] || statusLabel(item.code),
    message: item.message || fallbacks[item.code] || "Review the facility setup and requested booking window."
  };
}

function buildReservationLifecyclePayload(status, actionLabel) {
  const reason = window.prompt(`${actionLabel} reason (optional)`);
  if (reason === null) {
    return null;
  }
  const trimmedReason = reason.trim();
  return trimmedReason ? { status, reason: trimmedReason } : { status };
}

function submitReservationLifecycleUpdate(onUpdateReservation, reservation, status, actionLabel) {
  const payload = buildReservationLifecyclePayload(status, actionLabel);
  if (payload) {
    onUpdateReservation(reservation.reservationId, payload);
  }
}

function toDateInput(date) {
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 10);
}

function toDateTimeLocalInput(date) {
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 16);
}

function isoToDateTimeLocalInput(value) {
  return value ? toDateTimeLocalInput(new Date(value)) : "";
}

function scheduleDayWindow(dateValue) {
  const startsAt = new Date(`${dateValue || toDateInput(new Date())}T00:00:00`);
  const endsAt = new Date(startsAt);
  endsAt.setDate(endsAt.getDate() + 1);
  return { startsAt, endsAt };
}

function buildScheduleDays(dateValue) {
  const startsAt = new Date(`${dateValue || toDateInput(new Date())}T00:00:00`);
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(startsAt);
    date.setDate(startsAt.getDate() + index);
    const key = toDateInput(date);
    return {
      key,
      label: date.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      weekday: date.toLocaleDateString(undefined, { weekday: "short" }),
      window: scheduleDayWindow(key)
    };
  });
}

function overlapsScheduleDay(item, window) {
  return Date.parse(item.startsAt) < window.endsAt.getTime() &&
    window.startsAt.getTime() < Date.parse(item.endsAt);
}

function defaultScheduleReservationWindow(dateValue) {
  const startsAt = new Date(`${dateValue || toDateInput(new Date())}T09:00:00`);
  const endsAt = new Date(startsAt);
  endsAt.setHours(endsAt.getHours() + 1);
  return {
    startsAt: toDateTimeLocalInput(startsAt),
    endsAt: toDateTimeLocalInput(endsAt)
  };
}

function StatusBoardRow({
  child = false,
  isEditing,
  isUpdating,
  onDoneEditing,
  onEditStatus,
  onResumeFiring,
  onSetCeaseFire,
  onStatusChange,
  resource
}) {
  const controlState = getStatusBoardControlState(resource);
  return (
    <div className={`access-row resource-status-row status-${resource.status || "open"} ${child ? "is-child" : ""}`}>
      <div className="resource-status-indicator" aria-hidden="true" />
      <div className="resource-status-copy">
        <strong>{resource.name}</strong>
        <div className="resource-status-facts">
          <span>{resourceRuleTypeLabel(resource.type)}</span>
          <span>{formatDistance(resource)}</span>
          {resource.availabilityPolicy?.notes && <span>{resource.availabilityPolicy.notes}</span>}
        </div>
      </div>
      <div className="resource-state resource-status-state">
        <span className={`member-status status-${resource.status || "open"}`}>{statusLabel(resource.status || "open")}</span>
        <span className={`app-state resource-control-badge is-${controlState.tone}`}>
          {controlState.label}
        </span>
      </div>
      <div className="resource-status-actions">
        {isEditing ? (
          <label>
            <span>Status</span>
            <select
              value={resource.status || "open"}
              disabled={isUpdating}
              onChange={(event) => onStatusChange(resource.resourceId, event.target.value)}
            >
              <option value="open">Open</option>
              <option value="maintenance">Maintenance</option>
              <option value="closed">Closed</option>
              <option value="temporarily_reserved">Reserved</option>
            </select>
          </label>
        ) : (
          <button
            type="button"
            className="secondary-button"
            onClick={() => onEditStatus(resource.resourceId)}
          >
            Change status
          </button>
        )}
        {isEditing && (
          <button
            type="button"
            className="secondary-button"
            onClick={onDoneEditing}
          >
            Done
          </button>
        )}
        {resource.targetControl?.status === "target_check" ? (
          <button type="button" disabled={isUpdating} onClick={() => onResumeFiring(resource)}>
            Resume firing
          </button>
        ) : (
          <button type="button" disabled={isUpdating} onClick={() => onSetCeaseFire(resource)}>
            Cease fire
          </button>
        )}
      </div>
    </div>
  );
}

function FacilitySelectOptions({
  childResourcesByParentId = new Map(),
  includeType = false,
  parentResources = [],
  resourceById = new Map()
}) {
  const options = [];
  const pushOptions = (resource, depth = 0) => {
    const level = resourceHierarchyLevel(resource, resourceById);
    const prefix = depth > 0 ? `${"\u00a0\u00a0".repeat(depth)}- ` : "";
    const label = includeType
      ? `${prefix}${resource.name} (${hierarchyLevelLabel(level)})`
      : `${prefix}${resource.name}`;
    options.push(
      <option value={resource.resourceId} key={resource.resourceId}>
        {label}
      </option>
    );
    (childResourcesByParentId.get(resource.resourceId) || []).forEach((child) => pushOptions(child, depth + 1));
  };
  parentResources.forEach((resource) => pushOptions(resource));
  return <>{options}</>;
}

export function ResourcesPage({
  availabilityForm,
  availabilityResult,
  checkingAvailability,
  checkingReservationConflicts,
  loading,
  onCheckAvailability,
  onCheckReservationConflicts,
  onCloseResourceDetail,
  onCreateResourceBlock,
  onCreateResource,
  onApplyLibertyFacilityTemplate,
  onExportResources,
  onCreateReservation,
  onExportBlocks,
  onExportReservations,
  onOpenResourceDetail,
  onUpdateResourceBlock,
  onUpdateResource,
  onUpdateReservation,
  resourceBlockForm,
  resourceBlocks,
  resourceDetail,
  resourceDetailLoading,
  resourceFilters,
  resourceReservationConflicts,
  resourceReservations,
  resources,
  members = [],
  resourceForm,
  reservationForm,
  exportingResources,
  exportingResourceBlocks,
  exportingReservations,
  savingResourceBlock,
  savingResource,
  savingReservation,
  selectedRange,
  selectedResourceDetailId,
  setAvailabilityForm,
  setResourceFilters,
  setResourceBlockForm,
  setResourceForm,
  setReservationForm,
  summary,
  updatingResourceBlockId,
  updatingResourceId,
  updatingReservationId
}) {
  const [scheduleDate, setScheduleDate] = useState(() => toDateInput(new Date()));
  const [blockStatusFilter, setBlockStatusFilter] = useState("active");
  const [blockTypeFilter, setBlockTypeFilter] = useState("");
  const [blockResourceFilter, setBlockResourceFilter] = useState("");
  const [blockSearchFilter, setBlockSearchFilter] = useState("");
  const [reservationStatusFilter, setReservationStatusFilter] = useState("");
  const [reservationResourceFilter, setReservationResourceFilter] = useState("");
  const [reservationSourceFilter, setReservationSourceFilter] = useState("");
  const [reservationMemberFilter, setReservationMemberFilter] = useState("");
  const [reservationSearchFilter, setReservationSearchFilter] = useState("");
  const [selectedReservationId, setSelectedReservationId] = useState("");
  const [activeResourceSection, setActiveResourceSection] = useState(() => {
    if (typeof window === "undefined") {
      return "inventory";
    }
    const hash = window.location.hash.replace("#resource-", "");
    return normalizeResourceSection(hash);
  });
  const [resourceCreateOpen, setResourceCreateOpen] = useState(false);
  const [resourceRuleEditorOpen, setResourceRuleEditorOpen] = useState(false);
  const [statusEditorResourceId, setStatusEditorResourceId] = useState("");
  const [ruleEditorResourceId, setRuleEditorResourceId] = useState("");
  const [expandedResourceGroupIds, setExpandedResourceGroupIds] = useState(() => new Set());
  const [expandedStatusGroupIds, setExpandedStatusGroupIds] = useState(() => new Set());
  const [expandedRuleGroupIds, setExpandedRuleGroupIds] = useState(() => new Set());
  const [ruleDraft, setRuleDraft] = useState(() => operatingRulesDraftFromResource());
  useEffect(() => {
    function syncResourceSectionFromHash() {
      const hash = window.location.hash.replace("#resource-", "");
      setActiveResourceSection(normalizeResourceSection(hash));
    }

    syncResourceSectionFromHash();
    window.addEventListener("hashchange", syncResourceSectionFromHash);
    return () => window.removeEventListener("hashchange", syncResourceSectionFromHash);
  }, []);
  const facilityResources = useMemo(() => resources.filter((resource) => !isArmoryResource(resource)), [resources]);
  const resourceById = useMemo(() => buildResourceMap(facilityResources), [facilityResources]);
  const childResourcesByParentId = useMemo(() => buildChildrenByParentId(facilityResources), [facilityResources]);
  const rootResources = useMemo(() => (
    facilityResources.filter((resource) => !resource.parentResourceId)
  ), [facilityResources]);
  const zoneResources = useMemo(() => (
    facilityResources.filter((resource) => resourceHierarchyLevel(resource, resourceById) === "zone")
  ), [facilityResources, resourceById]);
  const rangeResources = useMemo(() => (
    facilityResources.filter((resource) => resourceHierarchyLevel(resource, resourceById) === "range")
  ), [facilityResources, resourceById]);
  const laneResources = useMemo(() => (
    facilityResources.filter((resource) => resourceHierarchyLevel(resource, resourceById) === "lane")
  ), [facilityResources, resourceById]);
  const showLibertyFacilityTemplate = facilityResources.length === 0 &&
    Boolean(onApplyLibertyFacilityTemplate) &&
    /liberty long range/i.test([
      selectedRange?.name,
      selectedRange?.slug,
      selectedRange?.profile?.displayName,
      selectedRange?.profile?.website
    ].filter(Boolean).join(" "));
  const inventoryResourceIdSet = useMemo(() => {
    if (!resourceFiltersActive(resourceFilters)) {
      return new Set(facilityResources.map((resource) => resource.resourceId));
    }
    const nextIds = new Set();
    const addAncestors = (resource) => {
      let current = resource;
      const visited = new Set();
      while (current?.parentResourceId && resourceById.has(current.parentResourceId) && !visited.has(current.resourceId)) {
        visited.add(current.resourceId);
        const parent = resourceById.get(current.parentResourceId);
        nextIds.add(parent.resourceId);
        current = parent;
      }
    };
    const addDescendants = (resource) => {
      collectDescendantResources(resource, childResourcesByParentId).forEach((descendant) => {
        nextIds.add(descendant.resourceId);
      });
    };
    facilityResources.forEach((resource) => {
      if (!resourceMatchesFilters(resource, resourceFilters, resourceById)) {
        return;
      }
      nextIds.add(resource.resourceId);
      addAncestors(resource);
      addDescendants(resource);
    });
    return nextIds;
  }, [childResourcesByParentId, facilityResources, resourceById, resourceFilters]);
  const inventoryParentResources = useMemo(() => (
    rootResources.filter((resource) => inventoryResourceIdSet.has(resource.resourceId))
  ), [inventoryResourceIdSet, rootResources]);
  const inventoryChildResourcesByParentId = useMemo(() => {
    const nextMap = new Map();
    childResourcesByParentId.forEach((children, parentResourceId) => {
      const visibleChildren = children.filter((child) => inventoryResourceIdSet.has(child.resourceId));
      if (visibleChildren.length > 0) {
        nextMap.set(parentResourceId, visibleChildren);
      }
    });
    return nextMap;
  }, [childResourcesByParentId, inventoryResourceIdSet]);
  const ruleEditorResource = facilityResources.find((resource) => resource.resourceId === ruleEditorResourceId);
  const qualificationCatalog = useMemo(() => (
    summary?.config?.settings?.qualificationCatalog || []
  ), [summary?.config?.settings?.qualificationCatalog]);
  const selectedRuleQualificationKeys = useMemo(() => parseQualificationKeyInput(ruleDraft.requiredQualificationKeys), [ruleDraft.requiredQualificationKeys]);
  const selectedResourceFormQualificationKeys = useMemo(() => parseQualificationKeyInput(resourceForm.requiredQualificationKeys), [resourceForm.requiredQualificationKeys]);
  const ruleQualificationOptions = useMemo(
    () => buildQualificationOptions(qualificationCatalog, selectedRuleQualificationKeys),
    [qualificationCatalog, selectedRuleQualificationKeys]
  );
  const resourceFormQualificationOptions = useMemo(
    () => buildQualificationOptions(qualificationCatalog, selectedResourceFormQualificationKeys),
    [qualificationCatalog, selectedResourceFormQualificationKeys]
  );
  const resourceFormLevel = resourceForm.parentResourceId
    ? (resourceById.get(resourceForm.parentResourceId)?.parentResourceId ? "lane" : "range")
    : (resourceForm.type === "lane" ? "lane" : "zone");
  const resourceFormParentOptions = resourceFormLevel === "lane" ? rangeResources : resourceFormLevel === "range" ? zoneResources : [];
  const showLaneCount = resourceFormLevel === "range";
  const showParentPicker = resourceFormLevel !== "zone";
  const scheduleDays = useMemo(() => buildScheduleDays(scheduleDate), [scheduleDate]);
  const calendarItemsByResourceId = useMemo(() => {
    const grouped = new Map();
    const ensureBucket = (resourceId, dayKey) => {
      const byDay = grouped.get(resourceId) || new Map();
      const bucket = byDay.get(dayKey) || { blocks: [], reservations: [] };
      byDay.set(dayKey, bucket);
      grouped.set(resourceId, byDay);
      return bucket;
    };
    const addCalendarItem = (resourceId, day, type, item) => {
      ensureBucket(resourceId, day.key)[type].push(item);
    };

    resourceBlocks
      .filter((block) => block.status === "active" && resourceById.has(block.resourceId))
      .forEach((block) => {
        scheduleDays
          .filter((day) => overlapsScheduleDay(block, day.window))
          .forEach((day) => {
            addCalendarItem(block.resourceId, day, "blocks", block);
            if (block.appliesToChildren) {
              (childResourcesByParentId.get(block.resourceId) || []).forEach((child) => {
                addCalendarItem(child.resourceId, day, "blocks", {
                  ...block,
                  inheritedFromResourceId: block.resourceId
                });
              });
            }
          });
      });

    resourceReservations
      .filter((reservation) => reservation.status === "confirmed")
      .forEach((reservation) => {
        const resource = resourceById.get(reservation.resourceId);
        if (!resource) {
          return;
        }
        scheduleDays
          .filter((day) => overlapsScheduleDay(reservation, day.window))
          .forEach((day) => {
            addCalendarItem(reservation.resourceId, day, "reservations", reservation);
            if (resource?.parentResourceId) {
              addCalendarItem(resource.parentResourceId, day, "reservations", {
                ...reservation,
                inheritedFromResourceId: reservation.resourceId
              });
            }
            (childResourcesByParentId.get(reservation.resourceId) || []).forEach((child) => {
              addCalendarItem(child.resourceId, day, "reservations", {
                ...reservation,
                inheritedFromResourceId: reservation.resourceId
              });
            });
          });
      });

    grouped.forEach((byDay) => {
      byDay.forEach((bucket) => {
        bucket.blocks.sort((left, right) => String(left.startsAt || "").localeCompare(String(right.startsAt || "")));
        bucket.reservations.sort((left, right) => String(left.startsAt || "").localeCompare(String(right.startsAt || "")));
      });
    });

    return grouped;
  }, [childResourcesByParentId, resourceBlocks, resourceById, resourceReservations, scheduleDays]);
  const facilityResourceBlocks = useMemo(() => (
    resourceBlocks.filter((block) => resourceById.has(block.resourceId))
  ), [resourceBlocks, resourceById]);
  const facilityResourceReservations = useMemo(() => (
    resourceReservations.filter((reservation) => resourceById.has(reservation.resourceId))
  ), [resourceReservations, resourceById]);
  const filteredBlocks = useMemo(() => (
    facilityResourceBlocks.filter((block) => {
      const search = blockSearchFilter.trim().toLowerCase();
      const haystack = [
        block.title,
        block.reason,
        block.type,
        block.status,
        resourceById.get(block.resourceId)?.name
      ].join(" ").toLowerCase();
      return (
        (!blockStatusFilter || block.status === blockStatusFilter) &&
        (!blockTypeFilter || block.type === blockTypeFilter) &&
        (!blockResourceFilter || block.resourceId === blockResourceFilter) &&
        (!search || haystack.includes(search))
      );
    })
  ), [blockResourceFilter, blockSearchFilter, blockStatusFilter, blockTypeFilter, facilityResourceBlocks, resourceById]);
  const blockExportFilters = {
    status: blockStatusFilter,
    type: blockTypeFilter,
    resourceId: blockResourceFilter,
    q: blockSearchFilter
  };
  const filteredReservations = useMemo(() => (
    facilityResourceReservations.filter((reservation) => {
      const search = reservationSearchFilter.trim().toLowerCase();
      const haystack = [
        reservation.customerName,
        reservation.contactEmail,
        reservation.memberSnapshot?.fullName,
        reservation.memberSnapshot?.email,
        reservation.notes,
        resourceById.get(reservation.resourceId)?.name
      ].join(" ").toLowerCase();
      return (
        (!reservationStatusFilter || reservation.status === reservationStatusFilter) &&
        (!reservationResourceFilter || reservation.resourceId === reservationResourceFilter) &&
        (!reservationSourceFilter || reservation.source === reservationSourceFilter) &&
        (!reservationMemberFilter || reservation.memberId === reservationMemberFilter) &&
        (!search || haystack.includes(search))
      );
    })
  ), [reservationMemberFilter, reservationResourceFilter, reservationSearchFilter, reservationSourceFilter, reservationStatusFilter, facilityResourceReservations, resourceById]);
  const reservationExportFilters = {
    status: reservationStatusFilter,
    resourceId: reservationResourceFilter,
    source: reservationSourceFilter,
    memberId: reservationMemberFilter,
    q: reservationSearchFilter
  };
  const selectedReservation = facilityResourceReservations.find((reservation) => reservation.reservationId === selectedReservationId);
  const reservableResourceCount = facilityResources.filter((resource) => resource.reservable).length;
  const bookableResourceCount = facilityResources.filter((resource) => isResourceBookable(resource)).length;
  const maintenanceResourceCount = facilityResources.filter((resource) => resource.status === "maintenance").length;
  const closedResourceCount = facilityResources.filter((resource) => ["closed", "temporarily_reserved"].includes(resource.status)).length;
  const openResourceCount = facilityResources.filter((resource) => (resource.status || "open") === "open").length;
  const activeHoldCount = facilityResourceBlocks.filter((block) => block.status === "active").length;
  const resourceSections = [
    { id: "inventory", label: "Facilities", meta: `${zoneResources.length} zones / ${rangeResources.length} ranges` },
    {
      id: "reservations",
      label: "Bookings",
      meta: `${facilityResourceReservations.length} bookings`
    },
    {
      id: "operating-rules",
      label: "Rules",
      meta: `${bookableResourceCount} bookable`
    },
    {
      id: "status-maintenance",
      label: "Status",
      meta: `${openResourceCount} open / ${activeHoldCount} holds`
    }
  ];

  function primeReservationFromSchedule(resourceId) {
    setReservationForm((current) => ({
      ...current,
      resourceId,
      ...defaultScheduleReservationWindow(scheduleDate)
    }));
    switchResourceSection("reservations");
  }

  function selectRuleResource(resourceId) {
    const resource = resourceById.get(resourceId);
    setRuleEditorResourceId(resourceId);
    setRuleDraft(operatingRulesDraftFromResource(resource));
    setResourceRuleEditorOpen(Boolean(resourceId));
  }

  function setRuleQualificationSelection(keys) {
    setRuleDraft((current) => ({
      ...current,
      requiredQualificationKeys: keys.join(", ")
    }));
  }

  function setResourceFormQualificationSelection(keys) {
    setResourceForm((current) => ({
      ...current,
      requiredQualificationKeys: keys.join(", ")
    }));
  }

  function setResourceFormLevel(level) {
    const parentOptions = level === "lane" ? rangeResources : level === "range" ? zoneResources : [];
    setResourceForm((current) => ({
      ...current,
      type: resourceTypeForHierarchyLevel(level, current.type),
      parentResourceId: level === "zone" ? "" : parentOptions[0]?.resourceId || "",
      laneCount: level === "range" ? current.laneCount : "0",
      laneNamePrefix: level === "lane" ? current.laneNamePrefix || "Lane" : current.laneNamePrefix
    }));
  }

  function saveOperatingRules(event) {
    event.preventDefault();
    if (!ruleEditorResourceId || !ruleEditorResource) {
      return;
    }
    onUpdateResource(ruleEditorResourceId, operatingRulesPatchFromDraft(ruleDraft, ruleEditorResource));
  }

  function clearRuleQualificationGate() {
    if (!ruleEditorResourceId) {
      return;
    }
    setRuleDraft((current) => ({
      ...current,
      requiredQualificationKeys: ""
    }));
    onUpdateResource(ruleEditorResourceId, {
      accessPolicy: {
        requiredQualificationKeys: []
      }
    });
  }

  function switchResourceSection(sectionId) {
    setActiveResourceSection(sectionId);
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", `#resource-${sectionId}`);
    }
  }

  function renderInventoryResourceNode(resource, depth = 0) {
    const children = inventoryChildResourcesByParentId.get(resource.resourceId) || [];
    const filtersActive = resourceFiltersActive(resourceFilters);
    const descendants = collectDescendantResources(resource, inventoryChildResourcesByParentId);
    const childrenVisible = children.length > 0 && (filtersActive || depth === 0 || expandedResourceGroupIds.has(resource.resourceId));
    const nextLevel = childHierarchyLabel(resource, resourceById);
    return (
      <article className={`resource-group hierarchy-depth-${depth}`} key={resource.resourceId}>
        <ResourceRow
          child={depth > 0}
          childResources={children}
          depth={depth}
          descendantCount={descendants.length}
          hierarchyLevel={resourceHierarchyLevel(resource, resourceById)}
          onOpenResourceDetail={onOpenResourceDetail}
          onUpdateResource={onUpdateResource}
          resource={resource}
          updatingResourceId={updatingResourceId}
        />
        {children.length > 0 && (
          <div className="resource-children-toggle">
            <span>
              {pluralizeHierarchyLevel(nextLevel, children.length)}
              {descendants.length > children.length ? ` / ${descendants.length} total below` : ""}
              {filtersActive ? " matching the current view" : ""}
            </span>
            {!filtersActive && depth > 0 && (
              <button
                type="button"
                className="secondary-button"
                onClick={() => toggleResourceGroup(resource.resourceId)}
              >
                {childrenVisible ? `Hide ${hierarchyLevelLabel(nextLevel).toLowerCase()}s` : `Show ${hierarchyLevelLabel(nextLevel).toLowerCase()}s`}
              </button>
            )}
          </div>
        )}
        {childrenVisible && (
          <div className="resource-children">
            {children.map((child) => renderInventoryResourceNode(child, depth + 1))}
          </div>
        )}
      </article>
    );
  }

  function renderRuleResourceNode(resource, depth = 0) {
    const children = childResourcesByParentId.get(resource.resourceId) || [];
    const childrenVisible = children.length > 0 && (depth === 0 || expandedRuleGroupIds.has(resource.resourceId));
    const nextLevel = childHierarchyLabel(resource, resourceById);
    return (
      <article className={`resource-rule-group hierarchy-depth-${depth}`} key={resource.resourceId}>
        <ResourceRuleRow
          child={depth > 0}
          depth={depth}
          hierarchyLevel={resourceHierarchyLevel(resource, resourceById)}
          qualificationCatalog={qualificationCatalog}
          onEditRules={selectRuleResource}
          resource={resource}
          selected={ruleEditorResourceId === resource.resourceId && resourceRuleEditorOpen}
        />
        {children.length > 0 && (
          <div className="resource-children-toggle">
            <span>{pluralizeHierarchyLevel(nextLevel, children.length)}</span>
            {depth > 0 && (
              <button
                type="button"
                className="secondary-button"
                onClick={() => toggleRuleGroup(resource.resourceId)}
              >
                {childrenVisible ? `Hide ${hierarchyLevelLabel(nextLevel).toLowerCase()}s` : `Show ${hierarchyLevelLabel(nextLevel).toLowerCase()}s`}
              </button>
            )}
          </div>
        )}
        {childrenVisible && (
          <div className="resource-children">
            {children.map((child) => renderRuleResourceNode(child, depth + 1))}
          </div>
        )}
      </article>
    );
  }

  function renderStatusResourceNode(resource, depth = 0) {
    const children = childResourcesByParentId.get(resource.resourceId) || [];
    const descendants = collectDescendantResources(resource, childResourcesByParentId);
    const statusScope = [resource, ...descendants];
    const groupStatusCounts = statusScope.reduce((counts, item) => ({
      ...counts,
      [item.status || "open"]: (counts[item.status || "open"] || 0) + 1
    }), {});
    const childrenVisible = children.length > 0 && (depth === 0 || expandedStatusGroupIds.has(resource.resourceId));
    const nextLevel = childHierarchyLabel(resource, resourceById);
    return (
      <article className={`resource-status-group hierarchy-depth-${depth}`} key={resource.resourceId}>
        <div className="resource-status-group-header">
          <div>
            <span>{resourceHierarchyLabel(resource, resourceById)}</span>
            <strong>{resource.name}</strong>
          </div>
          <div className="resource-status-group-counts">
            <span>{groupStatusCounts.open || 0} open</span>
            <span>{groupStatusCounts.maintenance || 0} maintenance</span>
            {descendants.length > 0 && <span>{descendants.length} below</span>}
          </div>
        </div>
        <div className="resource-status-group-list">
          <StatusBoardRow
            child={depth > 0}
            isEditing={statusEditorResourceId === resource.resourceId}
            isUpdating={updatingResourceId === resource.resourceId}
            key={resource.resourceId}
            onDoneEditing={() => setStatusEditorResourceId("")}
            onEditStatus={setStatusEditorResourceId}
            onResumeFiring={(targetResource) => onUpdateResource(targetResource.resourceId, {
              targetControl: {
                status: "firing_allowed",
                note: "Firing resumed",
                cycle: targetResource.targetControl?.cycle
              }
            })}
            onSetCeaseFire={(targetResource) => onUpdateResource(targetResource.resourceId, {
              targetControl: {
                status: "target_check",
                note: "Cease fire / target check in progress",
                cycle: targetResource.targetControl?.cycle
              }
            })}
            onStatusChange={(resourceId, status) => onUpdateResource(resourceId, { status })}
            resource={resource}
          />
          {children.length > 0 && (
            <div className="resource-children-toggle">
              <span>{pluralizeHierarchyLevel(nextLevel, children.length)}</span>
              {depth > 0 && (
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => toggleStatusGroup(resource.resourceId)}
                >
                  {childrenVisible ? `Hide ${hierarchyLevelLabel(nextLevel).toLowerCase()}s` : `Show ${hierarchyLevelLabel(nextLevel).toLowerCase()}s`}
                </button>
              )}
            </div>
          )}
          {childrenVisible && children.map((child) => renderStatusResourceNode(child, depth + 1))}
        </div>
      </article>
    );
  }

  function toggleResourceGroup(resourceId) {
    setExpandedResourceGroupIds((current) => {
      const next = new Set(current);
      if (next.has(resourceId)) {
        next.delete(resourceId);
      } else {
        next.add(resourceId);
      }
      return next;
    });
  }

  function toggleStatusGroup(resourceId) {
    setExpandedStatusGroupIds((current) => {
      const next = new Set(current);
      if (next.has(resourceId)) {
        next.delete(resourceId);
      } else {
        next.add(resourceId);
      }
      return next;
    });
  }

  function toggleRuleGroup(resourceId) {
    setExpandedRuleGroupIds((current) => {
      const next = new Set(current);
      if (next.has(resourceId)) {
        next.delete(resourceId);
      } else {
        next.add(resourceId);
      }
      return next;
    });
  }

  if (!selectedRange) {
    return null;
  }

  return (
    <section className="panel module-panel" id="resources">
      {loading && <div className="empty-state">Loading facilities...</div>}

      {!loading && selectedResourceDetailId && (
        <ResourceDetailView
          loading={resourceDetailLoading}
          onBack={onCloseResourceDetail}
          onOpenResourceDetail={onOpenResourceDetail}
          onPrimeReservation={primeReservationFromSchedule}
          onUpdateResource={onUpdateResource}
          calendarItemsByResourceId={calendarItemsByResourceId}
          resourceDetail={resourceDetail}
          resources={facilityResources}
          scheduleDate={scheduleDate}
          scheduleDays={scheduleDays}
          setScheduleDate={setScheduleDate}
          updatingResourceId={updatingResourceId}
        />
      )}

      {!loading && !selectedResourceDetailId && (
        <>
          <div className="facility-status-strip" aria-label="Facility summary">
            <div>
              <span>Zones</span>
              <strong>{zoneResources.length}</strong>
            </div>
            <div>
              <span>Ranges</span>
              <strong>{rangeResources.length}</strong>
            </div>
            <div>
              <span>Lanes</span>
              <strong>{laneResources.length}</strong>
            </div>
            <div>
              <span>Reservable</span>
              <strong>{reservableResourceCount}</strong>
            </div>
          </div>

          <nav className="console-section-nav" aria-label="Facility sections">
            {resourceSections.map((section) => (
              <button
                type="button"
                className={activeResourceSection === section.id ? "is-active" : ""}
                key={section.id}
                onClick={() => switchResourceSection(section.id)}
              >
                <span>{section.label}</span>
                <small>{section.meta}</small>
              </button>
            ))}
          </nav>

          {activeResourceSection === "inventory" && (
          <div className="resource-management-grid resource-inventory-workbench" id="resource-inventory">
            {resourceCreateOpen && (
              <div className="resource-create-inline">
            <form className="member-form" onSubmit={onCreateResource}>
              <div className="panel-header">
                <h3>Add facility</h3>
                <p>Create a zone, range, or lane.</p>
                <button type="button" className="secondary-button" onClick={() => setResourceCreateOpen(false)}>
                  Cancel
                </button>
              </div>
              <div className="resource-model-note">
                <strong>Facility structure</strong>
                <span>Use zones for broad areas, ranges inside zones, and lanes inside ranges. Any level can be reservable.</span>
              </div>

              <div className="form-row">
                <label>
                  Level
                  <select
                    value={resourceFormLevel}
                    onChange={(event) => setResourceFormLevel(event.target.value)}
                  >
                    <option value="zone">Zone</option>
                    <option value="range">Range</option>
                    <option value="lane">Lane</option>
                  </select>
                </label>
                <label>
                  Name
                  <input
                    value={resourceForm.name}
                    onChange={(event) => setResourceForm((current) => ({ ...current, name: event.target.value }))}
                    placeholder={resourceFormLevel === "zone" ? "Long-Range Zone" : resourceFormLevel === "range" ? "1000-Yard Range" : "Camera Lane 1"}
                    required
                  />
                </label>
              </div>

              {showParentPicker && (
                <label>
                  {resourceFormLevel === "range" ? "Inside zone" : "Inside range"}
                  <select
                    value={resourceForm.parentResourceId}
                    onChange={(event) => setResourceForm((current) => ({ ...current, parentResourceId: event.target.value }))}
                    required={resourceFormLevel === "lane"}
                  >
                    <option value="">{resourceFormLevel === "range" ? "Top level / no zone yet" : "Select range"}</option>
                    {resourceFormParentOptions.map((parent) => (
                      <option value={parent.resourceId} key={parent.resourceId}>{resourcePathLabel(parent, resourceById)}</option>
                    ))}
                  </select>
                </label>
              )}

              {showLaneCount && (
                <div className="form-row">
                  <label>
                    Auto-create lanes
                    <input
                      type="number"
                      min="0"
                      max="20"
                      value={resourceForm.laneCount}
                      onChange={(event) => setResourceForm((current) => ({ ...current, laneCount: event.target.value }))}
                    />
                  </label>
                  <label>
                    Lane name prefix
                    <input
                      value={resourceForm.laneNamePrefix}
                      onChange={(event) => setResourceForm((current) => ({ ...current, laneNamePrefix: event.target.value }))}
                      placeholder="Lane or Position"
                    />
                  </label>
                </div>
              )}

              <div className="form-row">
                <label>
                  Operational status
                  <select
                    value={resourceForm.status}
                    onChange={(event) => setResourceForm((current) => ({ ...current, status: event.target.value }))}
                  >
                    <option value="open">Open</option>
                    <option value="closed">Closed</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="temporarily_reserved">Temporarily reserved</option>
                  </select>
                </label>
                <label>
                  Target control
                  <select
                    value={resourceForm.targetControlStatus}
                    onChange={(event) => setResourceForm((current) => ({ ...current, targetControlStatus: event.target.value }))}
                  >
                    <option value="firing_allowed">Firing allowed</option>
                    <option value="target_check">Cease fire / target check</option>
                    <option value="paused">Paused</option>
                    <option value="closed">Closed</option>
                  </select>
                </label>
              </div>

              <label>
                Target control note
                <input
                  value={resourceForm.targetControlNote}
                  onChange={(event) => setResourceForm((current) => ({ ...current, targetControlNote: event.target.value }))}
                  placeholder="Cease fire until staff clears the bay"
                />
              </label>

              <div className="form-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={resourceForm.targetCycleEnabled}
                    onChange={(event) => setResourceForm((current) => ({ ...current, targetCycleEnabled: event.target.checked }))}
                  />
                  Use cease-fire / target-check cycle
                </label>
                <label>
                  Cycle starts
                  <input
                    type="datetime-local"
                    value={resourceForm.targetCycleStartsAt}
                    onChange={(event) => setResourceForm((current) => ({ ...current, targetCycleStartsAt: event.target.value }))}
                    disabled={!resourceForm.targetCycleEnabled}
                  />
                </label>
              </div>

              <div className="form-row">
                <label>
                  Hot range minutes
                  <input
                    type="number"
                    min="0"
                    max="240"
                    value={resourceForm.targetCycleFiringMinutes}
                    onChange={(event) => setResourceForm((current) => ({ ...current, targetCycleFiringMinutes: event.target.value }))}
                    disabled={!resourceForm.targetCycleEnabled}
                  />
                </label>
                <label>
                  Cease-fire minutes
                  <input
                    type="number"
                    min="0"
                    max="120"
                    value={resourceForm.targetCycleCheckMinutes}
                    onChange={(event) => setResourceForm((current) => ({ ...current, targetCycleCheckMinutes: event.target.value }))}
                    disabled={!resourceForm.targetCycleEnabled}
                  />
                </label>
              </div>

              <label>
                Cycle note
                <input
                  value={resourceForm.targetCycleNote}
                  onChange={(event) => setResourceForm((current) => ({ ...current, targetCycleNote: event.target.value }))}
                  placeholder="Cease fire every hour so shooters can check or replace targets"
                  disabled={!resourceForm.targetCycleEnabled}
                />
              </label>

              <div className="form-row">
                <label>
                  Shooter capacity
                  <input
                    type="number"
                    min="0"
                    value={resourceForm.shooterCapacity}
                    onChange={(event) => setResourceForm((current) => ({ ...current, shooterCapacity: event.target.value }))}
                  />
                </label>
                <label>
                  Lanes
                  <input
                    type="number"
                    min="0"
                    value={resourceForm.laneCapacity}
                    onChange={(event) => setResourceForm((current) => ({ ...current, laneCapacity: event.target.value }))}
                  />
                </label>
              </div>

              <div className="form-row">
                <label>
                  Minimum distance
                  <input
                    type="number"
                    min="0"
                    value={resourceForm.distanceMin}
                    onChange={(event) => setResourceForm((current) => ({ ...current, distanceMin: event.target.value }))}
                  />
                </label>
                <label>
                  Maximum distance
                  <input
                    type="number"
                    min="0"
                    value={resourceForm.distanceMax}
                    onChange={(event) => setResourceForm((current) => ({ ...current, distanceMax: event.target.value }))}
                  />
                </label>
              </div>

              <label>
                Fixed distances
                <input
                  value={resourceForm.fixedDistances}
                  onChange={(event) => setResourceForm((current) => ({ ...current, fixedDistances: event.target.value }))}
                  placeholder="25, 50, 100"
                />
              </label>

              <div className="resource-model-note">
                <strong>Equipment and rental tracking</strong>
                <span>
                  Mark rental firearms, equipment, or rentable gear here when the asset needs front-desk status and maintenance tracking.
                </span>
              </div>

              <div className="form-row">
                <label>
                  Environment
                  <select
                    value={resourceForm.environment}
                    onChange={(event) => setResourceForm((current) => ({ ...current, environment: event.target.value }))}
                  >
                    <option value="outdoor">Outdoor</option>
                    <option value="indoor">Indoor</option>
                    <option value="mixed">Mixed</option>
                    <option value="portable">Portable</option>
                  </select>
                </label>
                <label>
                  Asset class
                  <input
                    value={resourceForm.assetClass}
                    onChange={(event) => setResourceForm((current) => ({ ...current, assetClass: event.target.value }))}
                    placeholder="Rental firearm, target carrier, spotting scope"
                  />
                </label>
              </div>

              <div className="form-row">
                <label>
                  Serial or tag
                  <input
                    value={resourceForm.serialNumber}
                    onChange={(event) => setResourceForm((current) => ({ ...current, serialNumber: event.target.value }))}
                    placeholder="RG-RNT-1024"
                  />
                </label>
                <label>
                  Rental status
                  <select
                    value={resourceForm.rentalStatus}
                    onChange={(event) => setResourceForm((current) => ({ ...current, rentalStatus: event.target.value }))}
                  >
                    <option value="not_rental">Not a rental</option>
                    <option value="available">Available</option>
                    <option value="checked_out">Checked out</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="retired">Retired</option>
                  </select>
                </label>
                <label>
                  Maintenance due
                  <input
                    type="date"
                    value={resourceForm.maintenanceDueAt}
                    onChange={(event) => setResourceForm((current) => ({ ...current, maintenanceDueAt: event.target.value }))}
                  />
                </label>
              </div>

              <div className="resource-model-note">
                <strong>Maintenance plan</strong>
                <span>
                  Use this for facility upkeep such as berm checks, steel inspections, access-road work, target-pit cleanup, and bench repairs.
                </span>
              </div>

              <div className="form-row">
                <label>
                  Plan name
                  <input
                    value={resourceForm.maintenancePlanName}
                    onChange={(event) => setResourceForm((current) => ({ ...current, maintenancePlanName: event.target.value }))}
                    placeholder="Monthly berm and target-pit inspection"
                  />
                </label>
                <label>
                  Every days
                  <input
                    type="number"
                    min="0"
                    max="3650"
                    value={resourceForm.maintenanceIntervalDays}
                    onChange={(event) => setResourceForm((current) => ({ ...current, maintenanceIntervalDays: event.target.value }))}
                    placeholder="30"
                  />
                </label>
                <label>
                  Responsible person
                  <input
                    value={resourceForm.maintenanceAssignedTo}
                    onChange={(event) => setResourceForm((current) => ({ ...current, maintenanceAssignedTo: event.target.value }))}
                    placeholder="Owner, RSO, or staff lead"
                  />
                </label>
              </div>

              <label>
                Checklist
                <textarea
                  rows={3}
                  value={resourceForm.maintenanceChecklist}
                  onChange={(event) => setResourceForm((current) => ({ ...current, maintenanceChecklist: event.target.value }))}
                  placeholder="Inspect berms, target stands, benches, signage, and access path"
                />
              </label>

              <div className="plan-rules">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={resourceForm.reservable}
                    onChange={(event) => setResourceForm((current) => ({ ...current, reservable: event.target.checked }))}
                  />
                  Reservable
                </label>
                <div className="form-row">
                  <label>
                    Min minutes
                    <input
                      type="number"
                      min="5"
                      value={resourceForm.minDurationMinutes}
                      onChange={(event) => setResourceForm((current) => ({ ...current, minDurationMinutes: event.target.value }))}
                    />
                  </label>
                  <label>
                    Max minutes
                    <input
                      type="number"
                      min="5"
                      value={resourceForm.maxDurationMinutes}
                      onChange={(event) => setResourceForm((current) => ({ ...current, maxDurationMinutes: event.target.value }))}
                    />
                  </label>
                </div>
                <div className="form-row">
                  <label>
                    Slot size
                    <input
                      type="number"
                      min="5"
                      value={resourceForm.slotSizeMinutes}
                      onChange={(event) => setResourceForm((current) => ({ ...current, slotSizeMinutes: event.target.value }))}
                    />
                  </label>
                  <label>
                    Buffer minutes
                    <input
                      type="number"
                      min="0"
                      value={resourceForm.bufferMinutes}
                      onChange={(event) => setResourceForm((current) => ({ ...current, bufferMinutes: event.target.value }))}
                    />
                  </label>
                  <label>
                    Change cutoff hours
                    <input
                      type="number"
                      min="0"
                      value={resourceForm.cancellationCutoffHours}
                      onChange={(event) => setResourceForm((current) => ({ ...current, cancellationCutoffHours: event.target.value }))}
                    />
                  </label>
                </div>
                <QualificationPicker
                  emptyMessage="No active qualifications are configured yet. Add them in Membership requirements before gating this facility."
                  onChange={setResourceFormQualificationSelection}
                  options={resourceFormQualificationOptions}
                  title="Required qualifications"
                  value={selectedResourceFormQualificationKeys}
                />
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={resourceForm.reservationFeeEnabled}
                    onChange={(event) => setResourceForm((current) => ({ ...current, reservationFeeEnabled: event.target.checked }))}
                  />
                  Require online reservation fee
                </label>
                <div className="form-row">
                  <label>
                    Fee amount
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={resourceForm.reservationFeeAmount}
                      onChange={(event) => setResourceForm((current) => ({ ...current, reservationFeeAmount: event.target.value }))}
                    />
                  </label>
                  <label>
                    Fee currency
                    <input
                      value={resourceForm.reservationFeeCurrency}
                      onChange={(event) => setResourceForm((current) => ({ ...current, reservationFeeCurrency: event.target.value }))}
                    />
                  </label>
                  <label>
                    Fee label
                    <input
                      value={resourceForm.reservationFeeLabel}
                      onChange={(event) => setResourceForm((current) => ({ ...current, reservationFeeLabel: event.target.value }))}
                    />
                  </label>
                </div>
              </div>

              <div className="resource-model-note">
                <strong>Facility availability window</strong>
                <span>
                  Set a facility-specific date range or daily booking window when this zone, range,
                  or lane follows different hours than the range.
                </span>
              </div>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={resourceForm.availabilityInheritRangeHours}
                  onChange={(event) => setResourceForm((current) => ({ ...current, availabilityInheritRangeHours: event.target.checked }))}
                />
                Also enforce range-wide business hours
              </label>

              <div className="form-row">
                <label>
                  Available from
                  <input
                    type="date"
                    value={resourceForm.availabilityStartsOn}
                    onChange={(event) => setResourceForm((current) => ({ ...current, availabilityStartsOn: event.target.value }))}
                  />
                </label>
                <label>
                  Available until
                  <input
                    type="date"
                    value={resourceForm.availabilityEndsOn}
                    onChange={(event) => setResourceForm((current) => ({ ...current, availabilityEndsOn: event.target.value }))}
                  />
                </label>
              </div>

              <div className="form-row">
                <label>
                  Opens daily
                  <input
                    type="time"
                    value={resourceForm.availabilityOpenTime}
                    onChange={(event) => setResourceForm((current) => ({ ...current, availabilityOpenTime: event.target.value }))}
                  />
                </label>
                <label>
                  Closes daily
                  <input
                    type="time"
                    value={resourceForm.availabilityCloseTime}
                    onChange={(event) => setResourceForm((current) => ({ ...current, availabilityCloseTime: event.target.value }))}
                  />
                </label>
              </div>

              <label>
                Availability note
                <input
                  value={resourceForm.availabilityNotes}
                  onChange={(event) => setResourceForm((current) => ({ ...current, availabilityNotes: event.target.value }))}
                  placeholder="Steel range opens after staff inspection"
                />
              </label>

              <label>
                Notes
                <input
                  value={resourceForm.notes}
                  onChange={(event) => setResourceForm((current) => ({ ...current, notes: event.target.value }))}
                  placeholder="Outdoor rifle range, staff supervised"
                />
              </label>

              <button type="submit" disabled={savingResource || !resourceForm.name.trim()}>
                {savingResource ? "Adding..." : "Add facility"}
              </button>
            </form>
              </div>
            )}

            <div className="member-list resource-inventory-list">
              <div className="panel-header">
                <div>
                  <h3>Facilities</h3>
                  <p>Zones, ranges, and lanes in the owner’s operating layout.</p>
                </div>
                <div className="resource-inventory-actions">
                  <button
                    type="button"
                    className="resource-add-button"
                    onClick={() => setResourceCreateOpen((current) => !current)}
                  >
                    {resourceCreateOpen ? "Close add" : "Add facility"}
                  </button>
                  <button type="button" className="secondary-button" disabled={exportingResources || facilityResources.length === 0} onClick={onExportResources}>
                    {exportingResources ? "Exporting..." : "Export CSV"}
                  </button>
                </div>
              </div>
              <div className="form-grid compact-form-grid resource-filter-grid">
                <label className="resource-search-field">
                  Search
                  <input
                    value={resourceFilters.q}
                    onChange={(event) => setResourceFilters((current) => ({ ...current, q: event.target.value }))}
                    placeholder="Facility name or note"
                  />
                </label>
                <label>
                  Level
                  <select value={resourceFilters.type} onChange={(event) => setResourceFilters((current) => ({ ...current, type: event.target.value }))}>
                    <option value="">All levels</option>
                    <option value="zone">Zones</option>
                    <option value="range">Ranges</option>
                    <option value="lane">Lanes</option>
                  </select>
                </label>
                <label>
                  Status
                  <select value={resourceFilters.status} onChange={(event) => setResourceFilters((current) => ({ ...current, status: event.target.value }))}>
                    <option value="">All statuses</option>
                    <option value="open">Open</option>
                    <option value="closed">Closed</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="temporarily_reserved">Temporarily reserved</option>
                  </select>
                </label>
                <label>
                  Line status
                  <select value={resourceFilters.targetControlStatus} onChange={(event) => setResourceFilters((current) => ({ ...current, targetControlStatus: event.target.value }))}>
                    <option value="">All line states</option>
                    <option value="firing_allowed">Firing allowed</option>
                    <option value="target_check">Cease fire / target check</option>
                    <option value="paused">Paused</option>
                    <option value="closed">Closed</option>
                  </select>
                </label>
                <label>
                  Reservable
                  <select value={resourceFilters.reservable} onChange={(event) => setResourceFilters((current) => ({ ...current, reservable: event.target.value }))}>
                    <option value="">All</option>
                    <option value="true">Reservable</option>
                    <option value="false">Not reservable</option>
                  </select>
                </label>
                <button
                  type="button"
                  className="secondary-button"
                  disabled={!resourceFilters.type && !resourceFilters.status && !resourceFilters.targetControlStatus && !resourceFilters.reservable && !resourceFilters.q}
                  onClick={() => setResourceFilters({ type: "", status: "", targetControlStatus: "", reservable: "", q: "" })}
                >
                  Clear filters
                </button>
              </div>

              {facilityResources.length === 0 && (
                <div className="empty-state resource-template-empty">
                  <strong>No facilities yet.</strong>
                  {showLibertyFacilityTemplate ? (
                    <>
                      <span>Start with Liberty Long Range’s known range layout, then fine-tune rules from the inventory.</span>
                      <button type="button" disabled={savingResource} onClick={onApplyLibertyFacilityTemplate}>
                        {savingResource ? "Applying template..." : "Apply Liberty facility template"}
                      </button>
                    </>
                  ) : (
                    <span>Add the first zone, then build ranges and lanes beneath it.</span>
                  )}
                </div>
              )}
              {facilityResources.length > 0 && inventoryParentResources.length === 0 && (
                <div className="empty-state">No facilities match the current filters.</div>
              )}
              {inventoryParentResources.map((resource) => renderInventoryResourceNode(resource))}
            </div>
          </div>
          )}

          {activeResourceSection === "operating-rules" && (
          <div className="resource-access-grid" id="resource-access-restrictions">
            <div className="member-list resource-rule-overview">
              <div className="panel-header">
                <h3>Booking rules</h3>
                <p>Member booking windows, qualification gates, and cease-fire cycles.</p>
              </div>
              {facilityResources.length === 0 && <div className="empty-state">No facilities are configured yet.</div>}
              {rootResources.map((resource) => renderRuleResourceNode(resource))}
            </div>

            {resourceRuleEditorOpen && (
            <div className="resource-gate-editor-shell" role="dialog" aria-modal="true" aria-labelledby="resource-rule-editor-title">
            <div className="resource-gate-editor">
            <form className="member-form resource-rule-editor-form" onSubmit={saveOperatingRules}>
              <div className="panel-header">
                <div>
                  <h3 id="resource-rule-editor-title">Edit operating rules</h3>
                  <p>{ruleEditorResource ? `Rules for ${ruleEditorResource.name}.` : "Choose a facility and set the rules members and staff follow."}</p>
                </div>
                <button type="button" className="secondary-button" onClick={() => setResourceRuleEditorOpen(false)}>
                  Close
                </button>
              </div>

              <label>
                Facility
                <select
                  value={ruleEditorResourceId}
                  onChange={(event) => selectRuleResource(event.target.value)}
                  required
                >
                  <option value="">Select facility</option>
                  <FacilitySelectOptions
                    childResourcesByParentId={childResourcesByParentId}
                    includeType
                    parentResources={rootResources}
                        resourceById={resourceById}
                  />
                </select>
              </label>

              <div className="resource-rule-editor-section">
                <div className="resource-rule-editor-section-heading">
                  <strong>Booking</strong>
                  <span>Member booking limits and reservation fees.</span>
                </div>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={ruleDraft.reservable}
                    onChange={(event) => setRuleDraft((current) => ({ ...current, reservable: event.target.checked }))}
                  />
                  Members can reserve this facility
                </label>
                <div className="form-row">
                  <label>
                    Minimum booking minutes
                    <input
                      type="number"
                      min="5"
                      value={ruleDraft.minDurationMinutes}
                      onChange={(event) => setRuleDraft((current) => ({ ...current, minDurationMinutes: event.target.value }))}
                    />
                  </label>
                  <label>
                    Maximum booking minutes
                    <input
                      type="number"
                      min="5"
                      value={ruleDraft.maxDurationMinutes}
                      onChange={(event) => setRuleDraft((current) => ({ ...current, maxDurationMinutes: event.target.value }))}
                    />
                  </label>
                  <label>
                    Slot size minutes
                    <input
                      type="number"
                      min="5"
                      value={ruleDraft.slotSizeMinutes}
                      onChange={(event) => setRuleDraft((current) => ({ ...current, slotSizeMinutes: event.target.value }))}
                    />
                  </label>
                </div>
                <div className="form-row">
                  <label>
                    Buffer minutes
                    <input
                      type="number"
                      min="0"
                      value={ruleDraft.bufferMinutes}
                      onChange={(event) => setRuleDraft((current) => ({ ...current, bufferMinutes: event.target.value }))}
                    />
                  </label>
                  <label>
                    Change cutoff hours
                    <input
                      type="number"
                      min="0"
                      value={ruleDraft.cancellationCutoffHours}
                      onChange={(event) => setRuleDraft((current) => ({ ...current, cancellationCutoffHours: event.target.value }))}
                    />
                  </label>
                </div>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={ruleDraft.reservationFeeEnabled}
                    onChange={(event) => setRuleDraft((current) => ({ ...current, reservationFeeEnabled: event.target.checked }))}
                  />
                  Require online reservation fee
                </label>
                <div className="form-row">
                  <label>
                    Fee amount
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={ruleDraft.reservationFeeAmount}
                      onChange={(event) => setRuleDraft((current) => ({ ...current, reservationFeeAmount: event.target.value }))}
                      disabled={!ruleDraft.reservationFeeEnabled}
                    />
                  </label>
                  <label>
                    Fee currency
                    <input
                      value={ruleDraft.reservationFeeCurrency}
                      onChange={(event) => setRuleDraft((current) => ({ ...current, reservationFeeCurrency: event.target.value.toLowerCase() }))}
                      disabled={!ruleDraft.reservationFeeEnabled}
                    />
                  </label>
                  <label>
                    Fee label
                    <input
                      value={ruleDraft.reservationFeeLabel}
                      onChange={(event) => setRuleDraft((current) => ({ ...current, reservationFeeLabel: event.target.value }))}
                      disabled={!ruleDraft.reservationFeeEnabled}
                    />
                  </label>
                </div>
              </div>

              <div className="resource-rule-editor-section">
                <div className="resource-rule-editor-section-heading">
                  <strong>Member availability</strong>
                  <span>Controls what members see while the range opens facilities in phases.</span>
                </div>
                <div className="form-row">
                  <label>
                    Member-facing phase
                    <select
                      value={ruleDraft.publicationStatus}
                      onChange={(event) => setRuleDraft((current) => ({ ...current, publicationStatus: event.target.value }))}
                    >
                      <option value="open">Open for member reservations</option>
                      <option value="members_only">Members only</option>
                      <option value="limited_preview">Limited preview - visible, not self-bookable</option>
                      <option value="under_construction">Under construction</option>
                      <option value="seasonal_closed">Seasonally closed</option>
                    </select>
                  </label>
                  <label>
                    Member note
                    <input
                      value={ruleDraft.publicationNote}
                      onChange={(event) => setRuleDraft((current) => ({ ...current, publicationNote: event.target.value }))}
                      placeholder="Example: 1,000-yard gate opens Fridays-Sundays in September"
                    />
                  </label>
                </div>
              </div>

              <div className="resource-rule-editor-section">
                <div className="resource-rule-editor-section-heading">
                  <strong>Facility hours</strong>
                  <span>Use this when the facility follows a different schedule than the range.</span>
                </div>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={ruleDraft.availabilityInheritRangeHours}
                    onChange={(event) => setRuleDraft((current) => ({ ...current, availabilityInheritRangeHours: event.target.checked }))}
                  />
                  Also enforce range-wide business hours
                </label>
                <div className="form-row">
                  <label>
                    Available from
                    <input
                      type="date"
                      value={ruleDraft.availabilityStartsOn}
                      onChange={(event) => setRuleDraft((current) => ({ ...current, availabilityStartsOn: event.target.value }))}
                    />
                  </label>
                  <label>
                    Available until
                    <input
                      type="date"
                      value={ruleDraft.availabilityEndsOn}
                      onChange={(event) => setRuleDraft((current) => ({ ...current, availabilityEndsOn: event.target.value }))}
                    />
                  </label>
                </div>
                <div className="form-row">
                  <label>
                    Opens daily
                    <input
                      type="time"
                      value={ruleDraft.availabilityOpenTime}
                      onChange={(event) => setRuleDraft((current) => ({ ...current, availabilityOpenTime: event.target.value }))}
                    />
                  </label>
                  <label>
                    Closes daily
                    <input
                      type="time"
                      value={ruleDraft.availabilityCloseTime}
                      onChange={(event) => setRuleDraft((current) => ({ ...current, availabilityCloseTime: event.target.value }))}
                    />
                  </label>
                </div>
                <label>
                  Hours note
                  <input
                    value={ruleDraft.availabilityNotes}
                    onChange={(event) => setRuleDraft((current) => ({ ...current, availabilityNotes: event.target.value }))}
                    placeholder="Opens after morning inspection"
                  />
                </label>
              </div>

              <div className="resource-rule-editor-section">
                <div className="resource-rule-editor-section-heading">
                  <strong>Required qualifications</strong>
                  <span>Choose the qualifications a member must have before booking.</span>
                </div>
              <QualificationPicker
                emptyMessage="No active qualifications are configured yet. Add them in Membership requirements, then return here to gate this facility."
                onChange={setRuleQualificationSelection}
                options={ruleQualificationOptions}
                title="Required qualifications"
                value={selectedRuleQualificationKeys}
              />
              </div>

              <div className="resource-rule-editor-section">
                <div className="resource-rule-editor-section-heading">
                  <strong>Cease-fire rhythm</strong>
                  <span>Set a repeating hot-range and target-check cycle for facilities that need one.</span>
                </div>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={ruleDraft.targetCycleEnabled}
                    onChange={(event) => setRuleDraft((current) => ({ ...current, targetCycleEnabled: event.target.checked }))}
                  />
                  Use a repeating cease-fire cycle
                </label>
                <div className="form-row">
                  <label>
                    Cycle starts
                    <input
                      type="datetime-local"
                      value={ruleDraft.targetCycleStartsAt}
                      onChange={(event) => setRuleDraft((current) => ({ ...current, targetCycleStartsAt: event.target.value }))}
                      disabled={!ruleDraft.targetCycleEnabled}
                    />
                  </label>
                  <label>
                    Hot range minutes
                    <input
                      type="number"
                      min="0"
                      max="240"
                      value={ruleDraft.targetCycleFiringMinutes}
                      onChange={(event) => setRuleDraft((current) => ({ ...current, targetCycleFiringMinutes: event.target.value }))}
                      disabled={!ruleDraft.targetCycleEnabled}
                    />
                  </label>
                  <label>
                    Cease-fire minutes
                    <input
                      type="number"
                      min="0"
                      max="120"
                      value={ruleDraft.targetCycleCheckMinutes}
                      onChange={(event) => setRuleDraft((current) => ({ ...current, targetCycleCheckMinutes: event.target.value }))}
                      disabled={!ruleDraft.targetCycleEnabled}
                    />
                  </label>
                </div>
                <label>
                  Cycle note
                  <input
                    value={ruleDraft.targetCycleNote}
                    onChange={(event) => setRuleDraft((current) => ({ ...current, targetCycleNote: event.target.value }))}
                    placeholder="Example: 50 minutes hot, 10 minutes cease fire"
                    disabled={!ruleDraft.targetCycleEnabled}
                  />
                </label>
              </div>

              <div className="form-actions split-actions">
                <button type="submit" disabled={(ruleEditorResourceId && updatingResourceId === ruleEditorResourceId) || !ruleEditorResourceId}>
                  {ruleEditorResourceId && updatingResourceId === ruleEditorResourceId ? "Saving..." : "Save operating rules"}
                </button>
                <button
                  type="button"
                  className="secondary-button"
                  disabled={(ruleEditorResourceId && updatingResourceId === ruleEditorResourceId) || !ruleEditorResourceId || selectedRuleQualificationKeys.length === 0}
                  onClick={clearRuleQualificationGate}
                >
                  Clear qualification gate
                </button>
              </div>
            </form>
            </div>
            </div>
            )}

            <div className="member-list">
              <div className="panel-header">
                <h3>Qualification gates</h3>
                <p>Facilities that require a member qualification before booking.</p>
              </div>
              {facilityResources.filter((resource) => resource.accessPolicy?.requiredQualificationKeys?.length > 0).length === 0 && (
                <div className="empty-state">No qualification gates yet.</div>
              )}
              {facilityResources
                .filter((resource) => resource.accessPolicy?.requiredQualificationKeys?.length > 0)
                .map((resource) => (
                  <div className="access-row resource-rule-row is-restricted" key={resource.resourceId}>
                    <div className="resource-rule-copy">
                      <div className="resource-rule-heading">
                        <strong>{resource.name}</strong>
                        <span>{resourceRuleTypeLabel(resource.type)}</span>
                      </div>
                      <div className="resource-rule-summary">
                        <div className="resource-rule-summary-item is-gate">
                          <b>Gate</b>
                          <span>{formatRequiredQualifications(resource, qualificationCatalog)}</span>
                        </div>
                        <div className="resource-rule-summary-item is-booking">
                          <b>Booking</b>
                          <span>{formatRuleBookingSummary(resource)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="resource-state">
                      <span className="member-status status-pending">
                        {resource.accessPolicy.requiredQualificationKeys.length} qualification{resource.accessPolicy.requiredQualificationKeys.length === 1 ? "" : "s"} required
                      </span>
                    </div>
                    <div className="row-actions">
                      <button type="button" className="secondary-button" onClick={() => selectRuleResource(resource.resourceId)}>
                        Edit rules
                      </button>
                      <button
                        type="button"
                        disabled={updatingResourceId === resource.resourceId}
                        onClick={() => onUpdateResource(resource.resourceId, { accessPolicy: { requiredQualificationKeys: [] } })}
                      >
                        Remove gate
                      </button>
                    </div>
                    <div className="restriction-key-list">{formatRequiredQualifications(resource, qualificationCatalog)}</div>
                  </div>
                ))}
            </div>
          </div>
          )}

          {activeResourceSection === "status-maintenance" && (
          <div className="availability-block-grid" id="resource-blocks">
            <div className="member-list resource-status-board">
              <div className="panel-header">
                <h3>Facility status</h3>
                <p>{openResourceCount} open, {maintenanceResourceCount} maintenance, {closedResourceCount} closed or held, {activeHoldCount} active hold{activeHoldCount === 1 ? "" : "s"}.</p>
              </div>
              {facilityResources.length === 0 && <div className="empty-state">No facilities are configured yet.</div>}
              {rootResources.map((resource) => renderStatusResourceNode(resource))}
            </div>

            <TaskDisclosure
              actionLabel="Add"
              title="Add a temporary hold or maintenance window"
              description="Close booking windows for maintenance, events, or staff-only use."
            >
            <form className="member-form" onSubmit={onCreateResourceBlock}>
              <div className="panel-header">
                <h3>Add temporary hold</h3>
                <p>Hold or close a facility for a specific window.</p>
              </div>

              <label>
                Facility
                <select
                  value={resourceBlockForm.resourceId}
                  onChange={(event) => setResourceBlockForm((current) => ({ ...current, resourceId: event.target.value }))}
                  required
                >
                  <option value="">Select facility</option>
                  <FacilitySelectOptions
                    childResourcesByParentId={childResourcesByParentId}
                    parentResources={rootResources}
                        resourceById={resourceById}
                  />
                </select>
              </label>

              <div className="form-row">
                <label>
                  Block type
                  <select
                    value={resourceBlockForm.type}
                    onChange={(event) => setResourceBlockForm((current) => ({ ...current, type: event.target.value }))}
                  >
                    <option value="maintenance">Maintenance</option>
                    <option value="closure">Closure</option>
                    <option value="staff_hold">Staff hold</option>
                    <option value="event_hold">Event hold</option>
                    <option value="private_reservation">Private reservation</option>
                  </select>
                </label>
                <label>
                  Title
                  <input
                    value={resourceBlockForm.title}
                    onChange={(event) => setResourceBlockForm((current) => ({ ...current, title: event.target.value }))}
                    placeholder="Weekend match setup"
                    required
                  />
                </label>
              </div>

              <div className="form-row">
                <label>
                  Starts
                  <input
                    type="datetime-local"
                    value={resourceBlockForm.startsAt}
                    onChange={(event) => setResourceBlockForm((current) => ({ ...current, startsAt: event.target.value }))}
                    required
                  />
                </label>
                <label>
                  Ends
                  <input
                    type="datetime-local"
                    value={resourceBlockForm.endsAt}
                    onChange={(event) => setResourceBlockForm((current) => ({ ...current, endsAt: event.target.value }))}
                    required
                  />
                </label>
              </div>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={resourceBlockForm.appliesToChildren}
                  onChange={(event) => setResourceBlockForm((current) => ({ ...current, appliesToChildren: event.target.checked }))}
                />
                Apply to lower levels
              </label>

              <div className="form-row">
                <label>
                  Recurrence
                  <select
                    value={resourceBlockForm.recurrenceFrequency}
                    onChange={(event) => setResourceBlockForm((current) => ({ ...current, recurrenceFrequency: event.target.value }))}
                  >
                    <option value="none">One-time block</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </label>
                <label>
                  Every
                  <input
                    type="number"
                    min="1"
                    max="52"
                    value={resourceBlockForm.recurrenceInterval}
                    onChange={(event) => setResourceBlockForm((current) => ({ ...current, recurrenceInterval: event.target.value }))}
                    disabled={resourceBlockForm.recurrenceFrequency === "none"}
                  />
                </label>
              </div>

              <label>
                Reason
                <input
                  value={resourceBlockForm.reason}
                  onChange={(event) => setResourceBlockForm((current) => ({ ...current, reason: event.target.value }))}
                  placeholder="Reserved for staff-supervised setup"
                />
              </label>

              <button
                type="submit"
                disabled={savingResourceBlock || !resourceBlockForm.resourceId || !resourceBlockForm.title.trim()}
              >
                {savingResourceBlock ? "Adding..." : "Add block"}
              </button>
            </form>
            </TaskDisclosure>

            <div className="member-list">
              <div className="panel-header">
                <h3>Temporary holds and maintenance windows</h3>
                <p>Holds close booking windows without changing the facility profile.</p>
                <button
                  type="button"
                  className="secondary-button"
                  disabled={exportingResourceBlocks || facilityResourceBlocks.length === 0}
                  onClick={() => onExportBlocks(blockExportFilters)}
                >
                  {exportingResourceBlocks ? "Exporting..." : "Export CSV"}
                </button>
              </div>

              <label>
                Block status
                <select value={blockStatusFilter} onChange={(event) => setBlockStatusFilter(event.target.value)}>
                  <option value="">All statuses</option>
                  <option value="active">Active</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="completed">Completed</option>
                </select>
              </label>
              <label>
                Block type
                <select value={blockTypeFilter} onChange={(event) => setBlockTypeFilter(event.target.value)}>
                  <option value="">All types</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="closure">Closure</option>
                  <option value="staff_hold">Staff hold</option>
                  <option value="event_hold">Event hold</option>
                  <option value="private_reservation">Private reservation</option>
                </select>
              </label>
              <label>
                Search blocks
                <input
                  value={blockSearchFilter}
                  onChange={(event) => setBlockSearchFilter(event.target.value)}
                  placeholder="Search title, reason, type"
                />
              </label>

              <label>
                Facility
                <select value={blockResourceFilter} onChange={(event) => setBlockResourceFilter(event.target.value)}>
                  <option value="">All facilities</option>
                  <FacilitySelectOptions
                    childResourcesByParentId={childResourcesByParentId}
                    parentResources={rootResources}
                        resourceById={resourceById}
                  />
                </select>
              </label>

              {facilityResourceBlocks.length === 0 && <div className="empty-state">No availability blocks yet.</div>}
              {facilityResourceBlocks.length > 0 && filteredBlocks.length === 0 && (
                <div className="empty-state">No availability blocks match these filters.</div>
              )}
              {filteredBlocks.map((block) => (
                <ResourceBlockRow
                  block={block}
                  key={block.blockId}
                  onUpdateResourceBlock={onUpdateResourceBlock}
                  resource={resourceById.get(block.resourceId)}
                  resources={facilityResources}
                  updatingResourceBlockId={updatingResourceBlockId}
                />
              ))}
            </div>
          </div>
          )}

          {activeResourceSection === "reservations" && (
          <div className="availability-check-grid" id="resource-availability-check">
            <TaskDisclosure
              actionLabel="Check"
              title="Check a reservation window"
              description="Confirm a facility is open before booking it."
            >
            <form className="member-form" onSubmit={onCheckAvailability}>
              <div className="panel-header">
                <h3>Check availability</h3>
                <p>Check live status, booking rules, and active holds.</p>
              </div>

              <label>
                Facility
                <select
                  value={availabilityForm.resourceId}
                  onChange={(event) => setAvailabilityForm((current) => ({ ...current, resourceId: event.target.value }))}
                  required
                >
                  <option value="">Select facility</option>
                  <FacilitySelectOptions
                    childResourcesByParentId={childResourcesByParentId}
                    parentResources={rootResources}
                        resourceById={resourceById}
                  />
                </select>
              </label>

              <div className="form-row">
                <label>
                  Starts
                  <input
                    type="datetime-local"
                    value={availabilityForm.startsAt}
                    onChange={(event) => setAvailabilityForm((current) => ({ ...current, startsAt: event.target.value }))}
                    required
                  />
                </label>
                <label>
                  Ends
                  <input
                    type="datetime-local"
                    value={availabilityForm.endsAt}
                    onChange={(event) => setAvailabilityForm((current) => ({ ...current, endsAt: event.target.value }))}
                    required
                  />
                </label>
              </div>

              <button type="submit" disabled={checkingAvailability || !availabilityForm.resourceId}>
                {checkingAvailability ? "Checking..." : "Check availability"}
              </button>
            </form>
            </TaskDisclosure>

            <div className="member-list">
              <div className="panel-header">
                <h3>Availability result</h3>
              </div>

              {!availabilityResult && <div className="empty-state">No availability check yet.</div>}
              {availabilityResult && (
                <div className={`availability-result ${availabilityResult.available ? "is-available" : "is-unavailable"}`}>
                  <strong>{availabilityResult.available ? "Available" : "Unavailable"}</strong>
                  <span>
                    {availabilityResult.resource?.name || "Selected facility"} · {availabilityResult.durationMinutes} minutes
                  </span>

                  {availabilityResult.reasons?.length > 0 && (
                    <div className="availability-reasons">
                      {availabilityResult.reasons.map((item) => {
                        const copy = formatAvailabilityReason(item);
                        return (
                          <div key={item.code}>
                            <strong>{copy.title}</strong>
                            <span>{copy.message}</span>
                          </div>
                        );
                      })}
                      {availabilityResult.businessHours && (
                        <div>
                          <strong>Operating schedule</strong>
                          <span>{availabilityResult.businessHours.timezone} · {statusLabel(availabilityResult.businessHours.status)}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {availabilityResult.blockingBlocks?.length > 0 && (
                    <div className="availability-reasons">
                      <strong>Blocking availability blocks</strong>
                      {availabilityResult.blockingBlocks.map((block) => (
                        <div key={block.blockId}>
                          <strong>{block.title}</strong>
                          <span>{statusLabel(block.type)} · {formatBlockWindow(block)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {availabilityResult.blockingReservations?.length > 0 && (
                    <div className="availability-reasons">
                      <strong>Blocking reservations</strong>
                      <span>
                        A higher-level booking blocks lower levels for the same window.
                      </span>
                      {availabilityResult.blockingReservations.map((reservation) => (
                        <div key={reservation.reservationId}>
                          <strong>{reservation.customerName}</strong>
                          <span>{statusLabel(reservation.status)} · {formatBlockWindow(reservation)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          )}

          {activeResourceSection === "reservations" && (
          <div className="reservation-management-grid" id="resource-reservations">
            <TaskDisclosure
              actionLabel="Create"
              title="Create booking"
              description="Owner-managed booking for guests, staff, or exceptions."
            >
            <form className="member-form" onSubmit={onCreateReservation}>
              <div className="panel-header">
                <h3>Create booking</h3>
              </div>

              <label>
                Facility
                <select
                  value={reservationForm.resourceId}
                  onChange={(event) => setReservationForm((current) => ({ ...current, resourceId: event.target.value }))}
                  required
                >
                  <option value="">Select facility</option>
                  <FacilitySelectOptions
                    childResourcesByParentId={childResourcesByParentId}
                    parentResources={rootResources}
                        resourceById={resourceById}
                  />
                </select>
              </label>

              <div className="form-row">
                <label>
                  Customer name
                  <input
                    value={reservationForm.customerName}
                    onChange={(event) => setReservationForm((current) => ({ ...current, customerName: event.target.value }))}
                    placeholder="Alex Morgan"
                    required
                  />
                </label>
                <label>
                  Contact email
                  <input
                    type="email"
                    value={reservationForm.contactEmail}
                    onChange={(event) => setReservationForm((current) => ({ ...current, contactEmail: event.target.value }))}
                    placeholder="alex@example.com"
                  />
                </label>
              </div>

              <div className="form-row">
                <label>
                  Starts
                  <input
                    type="datetime-local"
                    value={reservationForm.startsAt}
                    onChange={(event) => setReservationForm((current) => ({ ...current, startsAt: event.target.value }))}
                    required
                  />
                </label>
                <label>
                  Ends
                  <input
                    type="datetime-local"
                    value={reservationForm.endsAt}
                    onChange={(event) => setReservationForm((current) => ({ ...current, endsAt: event.target.value }))}
                    required
                  />
                </label>
              </div>

              <label>
                Notes
                <input
                  value={reservationForm.notes}
                  onChange={(event) => setReservationForm((current) => ({ ...current, notes: event.target.value }))}
                  placeholder="Owner-created reservation"
                />
              </label>
              <label>
                Source
                <select
                  value={reservationForm.source}
                  onChange={(event) => setReservationForm((current) => ({ ...current, source: event.target.value }))}
                >
                  <option value="owner">Owner reservation</option>
                  <option value="walk_in">Walk-in visit</option>
                  <option value="event">Event hold</option>
                </select>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={reservationForm.overrideAvailability}
                  onChange={(event) => setReservationForm((current) => ({ ...current, overrideAvailability: event.target.checked }))}
                />
                Override availability checks
              </label>
              {reservationForm.overrideAvailability && (
                <label>
                  Override reason
                  <input
                    value={reservationForm.overrideReason}
                    onChange={(event) => setReservationForm((current) => ({ ...current, overrideReason: event.target.value }))}
                    placeholder="Owner approved double-booking"
                  />
                </label>
              )}
              <label>
                Reservation status
                <select
                  value={reservationForm.status || "confirmed"}
                  onChange={(event) => setReservationForm((current) => ({ ...current, status: event.target.value }))}
                >
                  <option value="confirmed">Confirmed booking</option>
                  <option value="standby">Standby / waitlist</option>
                </select>
              </label>

              <button
                type="submit"
                disabled={savingReservation || !reservationForm.resourceId || !reservationForm.customerName.trim()}
              >
                {savingReservation ? "Creating..." : "Create reservation"}
              </button>
            </form>
            </TaskDisclosure>

            <div className="member-list">
              <div className="panel-header">
                <h3>Bookings</h3>
                <button
                  type="button"
                  className="secondary-button"
                  disabled={exportingReservations || facilityResourceReservations.length === 0}
                  onClick={() => onExportReservations(reservationExportFilters)}
                >
                  {exportingReservations ? "Exporting..." : "Export CSV"}
                </button>
                <button
                  type="button"
                  className="secondary-button"
                  disabled={checkingReservationConflicts || facilityResourceReservations.length === 0}
                  onClick={() => onCheckReservationConflicts(reservationExportFilters)}
                >
                  {checkingReservationConflicts ? "Checking..." : "Check conflicts"}
                </button>
              </div>
              {resourceReservationConflicts && (
                <div className={resourceReservationConflicts.summary?.totalConflicts > 0 ? "notice is-error" : "notice"}>
                  <strong>{resourceReservationConflicts.summary?.totalConflicts || 0} reservation conflict{resourceReservationConflicts.summary?.totalConflicts === 1 ? "" : "s"}</strong>
                  {resourceReservationConflicts.summary?.byResource?.length > 0 && (
                    <span>
                      {resourceReservationConflicts.summary.byResource.map((item) => `${item.resourceName || "Facility"}: ${item.conflicts}`).join(" / ")}
                    </span>
                  )}
                </div>
              )}

              <div className="resource-reservation-filters">
                <label>
                  Reservation status
                  <select value={reservationStatusFilter} onChange={(event) => setReservationStatusFilter(event.target.value)}>
                    <option value="">All statuses</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="standby">Standby</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="completed">Completed</option>
                    <option value="no_show">No-show</option>
                  </select>
                </label>
                <label>
                  Facility
                  <select value={reservationResourceFilter} onChange={(event) => setReservationResourceFilter(event.target.value)}>
                    <option value="">All facilities</option>
                    <FacilitySelectOptions
                      childResourcesByParentId={childResourcesByParentId}
                      parentResources={rootResources}
                        resourceById={resourceById}
                    />
                  </select>
                </label>
                <label>
                  Search reservations
                  <input
                    value={reservationSearchFilter}
                    onChange={(event) => setReservationSearchFilter(event.target.value)}
                    placeholder="Search customer, email, notes"
                  />
                </label>
                <label>
                  Reservation source
                  <select value={reservationSourceFilter} onChange={(event) => setReservationSourceFilter(event.target.value)}>
                    <option value="">All reservations</option>
                    <option value="owner">Owner-created</option>
                    <option value="member">Member-created</option>
                    <option value="walk_in">Walk-in</option>
                    <option value="event">Event-created</option>
                    <option value="system">System-created</option>
                  </select>
                </label>
                <label>
                  Reservation member
                  <select value={reservationMemberFilter} onChange={(event) => setReservationMemberFilter(event.target.value)}>
                    <option value="">All members</option>
                    {members.map((member) => (
                      <option value={member.memberId} key={member.memberId}>
                        {member.fullName || member.email || "Member record"}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              {facilityResourceReservations.length === 0 && <div className="empty-state">No reservations yet.</div>}
              {facilityResourceReservations.length > 0 && filteredReservations.length === 0 && (
                <div className="empty-state">No reservations match these filters.</div>
              )}
              {selectedReservation && (
                <ReservationDetailPanel
                  key={selectedReservation.reservationId}
                  onClose={() => setSelectedReservationId("")}
                  onUpdateReservation={onUpdateReservation}
                  reservation={selectedReservation}
                  resource={resourceById.get(selectedReservation.resourceId)}
                  resources={facilityResources}
                  updatingReservationId={updatingReservationId}
                />
              )}
              {filteredReservations.map((reservation) => (
                <ResourceReservationRow
                  key={reservation.reservationId}
                  onOpenReservation={setSelectedReservationId}
                  onUpdateReservation={onUpdateReservation}
                  reservation={reservation}
                  resource={resourceById.get(reservation.resourceId)}
                  updatingReservationId={updatingReservationId}
                />
              ))}
            </div>
          </div>
          )}
        </>
      )}
    </section>
  );
}

function ResourceCalendarRow({ days, itemsByDay, onPrimeReservation, resource }) {
  const targetStatus = resource.targetControl?.status;
  const isClosed = ["closed", "maintenance", "temporarily_reserved"].includes(resource.status);
  const isTargetCheck = targetStatus === "target_check";
  const statusClass = isClosed ? "is-closed" : isTargetCheck ? "is-target-check" : "is-open";
  const statusText = isClosed ? statusLabel(resource.status) : isTargetCheck ? "Cease fire" : "Open";

  return (
    <>
      <div className="resource-calendar-resource">
        <strong>{resource.name}</strong>
        <span>{resourceTypeLabel(resource.type)}</span>
        <span>{formatReservationRules(resource)}</span>
        <span>{formatResourceAvailabilityPolicy(resource)}</span>
        <button
          type="button"
          className="secondary-button"
          disabled={!resource.reservable}
          onClick={() => onPrimeReservation(resource.resourceId)}
        >
          Reserve
        </button>
      </div>
      {days.map((day) => {
        const bucket = itemsByDay?.get(day.key) || { blocks: [], reservations: [] };
        const firstReservation = bucket.reservations[0];
        const firstBlock = bucket.blocks[0];
        return (
          <div className={`resource-calendar-cell ${statusClass}`} key={`${resource.resourceId}-${day.key}`}>
            <span className={`resource-calendar-status ${statusClass}`}>{statusText}</span>
            {bucket.reservations.length > 0 && (
              <span className="resource-calendar-event is-reserved">
                {bucket.reservations.length} reserved
                {firstReservation?.startsAt ? ` / ${formatTimeRange(firstReservation)}` : ""}
              </span>
            )}
            {bucket.blocks.length > 0 && (
              <span className="resource-calendar-event is-blocked">
                {bucket.blocks.length} blocked
                {firstBlock?.title ? ` / ${firstBlock.title}` : ""}
                {firstBlock?.inheritedFromResourceId ? " / inherited" : ""}
              </span>
            )}
            {bucket.reservations.length === 0 && bucket.blocks.length === 0 && (
              <span className="resource-calendar-empty">No bookings</span>
            )}
          </div>
        );
      })}
    </>
  );
}

function ReservationDetailPanel({ onClose, onUpdateReservation, reservation, resource, resources, updatingReservationId }) {
  const isUpdating = updatingReservationId === reservation.reservationId;
  const attendance = reservation.attendance || {};
  const [editForm, setEditForm] = useState({
    resourceId: reservation.resourceId || "",
    customerName: reservation.customerName || "",
    contactEmail: reservation.contactEmail || "",
    startsAt: isoToDateTimeLocalInput(reservation.startsAt),
    endsAt: isoToDateTimeLocalInput(reservation.endsAt),
    notes: reservation.notes || "",
    overrideAvailability: false,
    overrideReason: "",
    reason: ""
  });

  function submitReservationCorrection(event) {
    event.preventDefault();
    onUpdateReservation(reservation.reservationId, {
      resourceId: editForm.resourceId,
      customerName: editForm.customerName,
      contactEmail: editForm.contactEmail,
      startsAt: new Date(editForm.startsAt).toISOString(),
      endsAt: new Date(editForm.endsAt).toISOString(),
      notes: editForm.notes,
      overrideAvailability: editForm.overrideAvailability,
      overrideReason: editForm.overrideReason,
      reason: editForm.reason
    });
  }

  return (
    <div className="member-detail-section">
      <div className="panel-header">
        <div>
          <h4>Reservation detail</h4>
          <p>{reservation.customerName} on {resource?.name || reservation.resourceSnapshot?.name || "selected facility"}</p>
        </div>
        <button type="button" className="secondary-button" onClick={onClose}>Close</button>
      </div>
      <dl>
        <div>
          <dt>Status</dt>
          <dd>{statusLabel(reservation.status)}</dd>
        </div>
        <div>
          <dt>Source</dt>
          <dd>
            {statusLabel(reservation.source)}
            {reservation.memberSnapshot?.fullName ? ` / ${reservation.memberSnapshot.fullName}` : reservation.memberId ? " / member record linked" : ""}
          </dd>
        </div>
        {reservation.memberSnapshot && (
          <div>
            <dt>Member snapshot</dt>
            <dd>
              {reservation.memberSnapshot.email || "No email"}
              {reservation.memberSnapshot.membershipTypeId ? " / plan linked" : ""}
            </dd>
          </div>
        )}
        <div>
          <dt>Window</dt>
          <dd>{formatBlockWindow(reservation)}</dd>
        </div>
        <div>
          <dt>Contact</dt>
          <dd>{reservation.contactEmail || "Not recorded"}</dd>
        </div>
        <div>
          <dt>Attendance</dt>
          <dd>
            {attendance.status ? statusLabel(attendance.status) : "Not checked in"}
            {attendance.checkedInAt ? ` / in ${formatDateTime(attendance.checkedInAt)}` : ""}
            {attendance.checkedOutAt ? ` / out ${formatDateTime(attendance.checkedOutAt)}` : ""}
          </dd>
        </div>
        {reservation.cancellationReason && (
          <div>
            <dt>Cancellation</dt>
            <dd>
              {reservation.cancellationReason}
              {reservation.cancelledAt ? ` / ${formatDateTime(reservation.cancelledAt)}` : ""}
            </dd>
          </div>
        )}
        {reservation.override?.availability && (
          <div>
            <dt>Override</dt>
            <dd>
              {reservation.override.availability.reason || "Availability override"}
              {reservation.override.availability.reasonCodes?.length > 0 ? ` / ${reservation.override.availability.reasonCodes.join(", ")}` : ""}
            </dd>
          </div>
        )}
      </dl>
      <form className="member-form" onSubmit={submitReservationCorrection}>
        <div className="panel-header">
          <h4>Correct reservation</h4>
          <p>Update the booking details after staff review.</p>
        </div>
        <label>
          Facility
          <select
            value={editForm.resourceId}
            onChange={(event) => setEditForm((current) => ({ ...current, resourceId: event.target.value }))}
            required
          >
            {resources.map((item) => (
              <option value={item.resourceId} key={item.resourceId}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <div className="form-row">
          <label>
            Customer name
            <input
              value={editForm.customerName}
              onChange={(event) => setEditForm((current) => ({ ...current, customerName: event.target.value }))}
              required
            />
          </label>
          <label>
            Contact email
            <input
              type="email"
              value={editForm.contactEmail}
              onChange={(event) => setEditForm((current) => ({ ...current, contactEmail: event.target.value }))}
            />
          </label>
        </div>
        <div className="form-row">
          <label>
            Starts
            <input
              type="datetime-local"
              value={editForm.startsAt}
              onChange={(event) => setEditForm((current) => ({ ...current, startsAt: event.target.value }))}
              required
            />
          </label>
          <label>
            Ends
            <input
              type="datetime-local"
              value={editForm.endsAt}
              onChange={(event) => setEditForm((current) => ({ ...current, endsAt: event.target.value }))}
              required
            />
          </label>
        </div>
        <label>
          Notes
          <textarea
            value={editForm.notes}
            onChange={(event) => setEditForm((current) => ({ ...current, notes: event.target.value }))}
          />
        </label>
        <label>
          Change reason
          <textarea
            value={editForm.reason}
            onChange={(event) => setEditForm((current) => ({ ...current, reason: event.target.value }))}
          />
        </label>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={editForm.overrideAvailability}
            onChange={(event) => setEditForm((current) => ({ ...current, overrideAvailability: event.target.checked }))}
          />
          Override availability checks
        </label>
        {editForm.overrideAvailability && (
          <label>
            Override reason
            <input
              value={editForm.overrideReason}
              onChange={(event) => setEditForm((current) => ({ ...current, overrideReason: event.target.value }))}
              placeholder="Manager approved exception"
            />
          </label>
        )}
        <button type="submit" disabled={isUpdating || !editForm.resourceId || !editForm.customerName.trim()}>
          {isUpdating ? "Saving..." : "Save correction"}
        </button>
      </form>
      <div className="resource-detail-actions">
        <button
          type="button"
          disabled={isUpdating || Boolean(attendance.checkedInAt) || ["cancelled", "completed", "no_show"].includes(reservation.status)}
          onClick={() => onUpdateReservation(reservation.reservationId, { attendanceAction: "check_in" })}
        >
          Check in
        </button>
        <button
          type="button"
          disabled={isUpdating || !attendance.checkedInAt || Boolean(attendance.checkedOutAt) || ["cancelled", "completed", "no_show"].includes(reservation.status)}
          onClick={() => onUpdateReservation(reservation.reservationId, { attendanceAction: "check_out" })}
        >
          Check out
        </button>
        <button
          type="button"
          disabled={isUpdating || reservation.status === "confirmed"}
          onClick={() => submitReservationLifecycleUpdate(onUpdateReservation, reservation, "confirmed", "Promote to confirmed")}
        >
          Confirm
        </button>
        <button
          type="button"
          disabled={isUpdating || reservation.status === "standby"}
          onClick={() => submitReservationLifecycleUpdate(onUpdateReservation, reservation, "standby", "Move to standby")}
        >
          Standby
        </button>
        <button
          type="button"
          disabled={isUpdating || reservation.status === "cancelled"}
          onClick={() => submitReservationLifecycleUpdate(onUpdateReservation, reservation, "cancelled", "Cancel")}
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={isUpdating || reservation.status === "completed"}
          onClick={() => submitReservationLifecycleUpdate(onUpdateReservation, reservation, "completed", "Complete")}
        >
          Complete
        </button>
        <button
          type="button"
          disabled={isUpdating || reservation.status === "no_show"}
          onClick={() => submitReservationLifecycleUpdate(onUpdateReservation, reservation, "no_show", "Mark no-show")}
        >
          No-show
        </button>
      </div>
      <h4>Status history</h4>
      {reservation.updateHistory?.length > 0 ? (
        <div className="member-detail-list">
          {reservation.updateHistory.map((item, index) => {
            const changedAt = item.changedAt || item.updatedAt;
            const actorUserId = item.actorUserId || item.updatedByUserId;
            return (
              <div key={item.historyId || `${changedAt || "history"}-${index}`}>
                <strong>{item.action ? statusLabel(item.action) : `${statusLabel(item.from?.status)} to ${statusLabel(item.to?.status)}`}</strong>
                <span>
                  {item.from?.startsAt !== item.to?.startsAt || item.from?.endsAt !== item.to?.endsAt
                    ? `${formatDateTime(item.from?.startsAt)} to ${formatDateTime(item.to?.startsAt)}`
                    : formatBlockWindow({ startsAt: item.to?.startsAt, endsAt: item.to?.endsAt })}
                  {" / "}
                  {formatDateTime(changedAt)}
                </span>
                {actorUserId && <span>Updated by team member</span>}
                {item.reason && <span>Reason: {item.reason}</span>}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="empty-state">No reservation updates recorded yet.</div>
      )}
    </div>
  );
}

function ResourceReservationRow({ onOpenReservation, onUpdateReservation, reservation, resource, updatingReservationId }) {
  const isUpdating = updatingReservationId === reservation.reservationId;
  const attendance = reservation.attendance || {};

  return (
    <div className="resource-reservation-row">
      <div>
        <strong>{reservation.customerName}</strong>
        <span>{resource?.name || reservation.resourceSnapshot?.name || "Selected facility"} · {formatBlockWindow(reservation)}</span>
        {reservation.contactEmail && <span>{reservation.contactEmail}</span>}
        {reservation.memberId && (
          <span>
            Member reservation: {reservation.memberSnapshot?.fullName || "member record linked"}
            {reservation.memberSnapshot?.membershipTypeId ? " / plan linked" : ""}
          </span>
        )}
        {attendance.checkedInAt && (
          <span>
            Checked in {formatDateTime(attendance.checkedInAt)}
            {attendance.checkedOutAt ? ` / out ${formatDateTime(attendance.checkedOutAt)}` : ""}
          </span>
        )}
        {attendance.status === "no_show" && (
          <span>
            No-show recorded
            {attendance.noShowAt ? ` / ${formatDateTime(attendance.noShowAt)}` : ""}
          </span>
        )}
        {reservation.notes && <span>{reservation.notes}</span>}
        {reservation.cancellationReason && <span>Cancellation: {reservation.cancellationReason}</span>}
        {reservation.override?.availability && (
          <span>
            Override: {reservation.override.availability.reason || reservation.override.availability.reasonCodes?.join(", ") || "availability"}
          </span>
        )}
      </div>
      <div className="resource-state">
        <span className={`member-status status-${reservation.status}`}>{statusLabel(reservation.status)}</span>
        <span className="app-state">{statusLabel(reservation.source)}</span>
      </div>
      <div className="row-actions">
        <button type="button" className="secondary-button" onClick={() => onOpenReservation(reservation.reservationId)}>
          Details
        </button>
        <button
          type="button"
          disabled={isUpdating || Boolean(attendance.checkedInAt) || ["cancelled", "completed", "no_show"].includes(reservation.status)}
          onClick={() => onUpdateReservation(reservation.reservationId, { attendanceAction: "check_in" })}
        >
          Check in
        </button>
        <button
          type="button"
          disabled={isUpdating || !attendance.checkedInAt || Boolean(attendance.checkedOutAt) || ["cancelled", "completed", "no_show"].includes(reservation.status)}
          onClick={() => onUpdateReservation(reservation.reservationId, { attendanceAction: "check_out" })}
        >
          Check out
        </button>
        <button
          type="button"
          disabled={isUpdating || reservation.status === "cancelled"}
          onClick={() => submitReservationLifecycleUpdate(onUpdateReservation, reservation, "cancelled", "Cancel")}
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={isUpdating || reservation.status === "completed"}
          onClick={() => submitReservationLifecycleUpdate(onUpdateReservation, reservation, "completed", "Complete")}
        >
          Complete
        </button>
        <button
          type="button"
          disabled={isUpdating || reservation.status === "no_show"}
          onClick={() => submitReservationLifecycleUpdate(onUpdateReservation, reservation, "no_show", "Mark no-show")}
        >
          No-show
        </button>
      </div>
    </div>
  );
}

function QualificationPicker({ emptyMessage, onChange, options, title, value }) {
  const selectedKeys = new Set(value || []);

  function toggleKey(key) {
    const nextKeys = selectedKeys.has(key)
      ? (value || []).filter((item) => item !== key)
      : [...(value || []), key];
    onChange(nextKeys);
  }

  return (
    <fieldset className="qualification-picker">
      <legend>{title}</legend>
      {options.length === 0 ? (
        <div className="qualification-picker-empty">{emptyMessage}</div>
      ) : (
        <div className="qualification-picker-list">
          {options.map((option) => (
            <label className={`qualification-picker-option ${option.catalog ? "" : "is-legacy"}`} key={option.key}>
              <input
                type="checkbox"
                checked={selectedKeys.has(option.key)}
                onChange={() => toggleKey(option.key)}
              />
              <span>
                <strong>{option.label}</strong>
                <small>{option.description || option.key}</small>
              </span>
            </label>
          ))}
        </div>
      )}
    </fieldset>
  );
}

function ResourceRuleRow({ child = false, onEditRules, qualificationCatalog = [], resource, selected = false }) {
  const requiredQualifications = resource.accessPolicy?.requiredQualificationKeys || [];
  const bookable = isResourceBookable(resource);

  return (
    <div className={`access-row resource-rule-row ${child ? "is-child" : ""} ${selected ? "is-selected" : ""}`}>
      <div className="resource-rule-copy">
        <div className="resource-rule-heading">
          <strong>{resource.name}</strong>
          <span>{resourceRuleTypeLabel(resource.type)}</span>
        </div>
        <div className="resource-rule-summary">
          <div className="resource-rule-summary-item is-booking">
            <b>Booking</b>
            <span>{formatRuleBookingSummary(resource)}</span>
          </div>
          <div className="resource-rule-summary-item is-hours">
            <b>Hours</b>
            <span>{formatRuleAvailabilitySummary(resource)}</span>
          </div>
          <div className="resource-rule-summary-item is-cease-fire">
            <b>Cease-fire</b>
            <span>{formatRuleCeaseFireSummary(resource)}</span>
          </div>
        </div>
      </div>
      <div className="resource-state">
        <span className={`member-status status-${resource.availabilityPolicy?.publicationStatus || "open"}`}>
          {publicationStatusLabel(resource.availabilityPolicy?.publicationStatus || "open")}
        </span>
        <span className={`member-status ${bookable ? "status-active" : "status-draft"}`}>
          {bookable ? "Member booking on" : "Member booking off"}
        </span>
        <span className={`app-state ${requiredQualifications.length > 0 ? "is-enabled" : ""}`}>
          {requiredQualifications.length > 0 ? `${requiredQualifications.length} qualification${requiredQualifications.length === 1 ? "" : "s"} required` : "No qualification required"}
        </span>
      </div>
      <div className="row-actions">
        <button type="button" className="secondary-button" onClick={() => onEditRules(resource.resourceId)}>
          Edit rules
        </button>
      </div>
      {requiredQualifications.length > 0 && (
        <div className="restriction-key-list">{formatRequiredQualifications(resource, qualificationCatalog)}</div>
      )}
    </div>
  );
}

function ResourceBlockRow({ block, onUpdateResourceBlock, resource, resources, updatingResourceBlockId }) {
  const isUpdating = updatingResourceBlockId === block.blockId;
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    resourceId: block.resourceId || "",
    type: block.type || "staff_hold",
    status: block.status || "active",
    title: block.title || "",
    reason: block.reason || "",
    startsAt: isoToDateTimeLocalInput(block.startsAt),
    endsAt: isoToDateTimeLocalInput(block.endsAt),
    appliesToChildren: Boolean(block.appliesToChildren),
    recurrenceFrequency: block.recurrence?.frequency || "none",
    recurrenceInterval: String(block.recurrence?.interval || 1)
  });

  function submitBlockCorrection(event) {
    event.preventDefault();
    onUpdateResourceBlock(block.blockId, {
      resourceId: editForm.resourceId,
      type: editForm.type,
      status: editForm.status,
      title: editForm.title,
      reason: editForm.reason,
      startsAt: new Date(editForm.startsAt).toISOString(),
      endsAt: new Date(editForm.endsAt).toISOString(),
      appliesToChildren: editForm.appliesToChildren,
      recurrence: {
        frequency: editForm.recurrenceFrequency,
        interval: Number.parseInt(editForm.recurrenceInterval || "1", 10)
      }
    });
  }

  return (
    <div className="resource-block-row">
      <div>
        <strong>{block.title}</strong>
        <span>{resource?.name || "Selected facility"} · {statusLabel(block.type)} · {formatBlockWindow(block)}</span>
        {block.recurrence?.frequency && block.recurrence.frequency !== "none" && (
          <span>Repeats {statusLabel(block.recurrence.frequency)} every {block.recurrence.interval || 1}</span>
        )}
        {block.reason && <span>{block.reason}</span>}
      </div>
      <div className="resource-state">
        <span className={`member-status status-${block.status}`}>{statusLabel(block.status)}</span>
        {block.appliesToChildren && <span className="app-state">Lower levels</span>}
      </div>
      <div className="row-actions">
        <button
          type="button"
          disabled={isUpdating}
          onClick={() => setIsEditing((current) => !current)}
        >
          {isEditing ? "Close edit" : "Edit"}
        </button>
        <button
          type="button"
          disabled={isUpdating || block.status === "active"}
          onClick={() => onUpdateResourceBlock(block.blockId, { status: "active" })}
        >
          Reopen
        </button>
        <button
          type="button"
          disabled={isUpdating || block.status === "cancelled"}
          onClick={() => onUpdateResourceBlock(block.blockId, { status: "cancelled" })}
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={isUpdating || block.status === "completed"}
          onClick={() => onUpdateResourceBlock(block.blockId, { status: "completed" })}
        >
          Complete
        </button>
      </div>
      {isEditing && (
        <form className="member-form resource-block-edit-form" onSubmit={submitBlockCorrection}>
          <div className="form-row">
            <label>
              Facility
              <select
                value={editForm.resourceId}
                onChange={(event) => setEditForm((current) => ({ ...current, resourceId: event.target.value }))}
                required
              >
                {resources.map((item) => (
                  <option value={item.resourceId} key={item.resourceId}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Block type
              <select
                value={editForm.type}
                onChange={(event) => setEditForm((current) => ({ ...current, type: event.target.value }))}
              >
                <option value="maintenance">Maintenance</option>
                <option value="closure">Closure</option>
                <option value="staff_hold">Staff hold</option>
                <option value="event_hold">Event hold</option>
                <option value="private_reservation">Private reservation</option>
              </select>
            </label>
            <label>
              Status
              <select
                value={editForm.status}
                onChange={(event) => setEditForm((current) => ({ ...current, status: event.target.value }))}
              >
                <option value="active">Active</option>
                <option value="cancelled">Cancelled</option>
                <option value="completed">Completed</option>
              </select>
            </label>
          </div>
          <label>
            Title
            <input
              value={editForm.title}
              onChange={(event) => setEditForm((current) => ({ ...current, title: event.target.value }))}
              required
            />
          </label>
          <div className="form-row">
            <label>
              Starts
              <input
                type="datetime-local"
                value={editForm.startsAt}
                onChange={(event) => setEditForm((current) => ({ ...current, startsAt: event.target.value }))}
                required
              />
            </label>
            <label>
              Ends
              <input
                type="datetime-local"
                value={editForm.endsAt}
                onChange={(event) => setEditForm((current) => ({ ...current, endsAt: event.target.value }))}
                required
              />
            </label>
          </div>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={editForm.appliesToChildren}
              onChange={(event) => setEditForm((current) => ({ ...current, appliesToChildren: event.target.checked }))}
            />
            Apply to lower levels
          </label>
          <div className="form-row">
            <label>
              Recurrence
              <select
                value={editForm.recurrenceFrequency}
                onChange={(event) => setEditForm((current) => ({ ...current, recurrenceFrequency: event.target.value }))}
              >
                <option value="none">Does not repeat</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </label>
            <label>
              Every
              <input
                min="1"
                type="number"
                value={editForm.recurrenceInterval}
                onChange={(event) => setEditForm((current) => ({ ...current, recurrenceInterval: event.target.value }))}
              />
            </label>
          </div>
          <label>
            Reason
            <textarea
              value={editForm.reason}
              onChange={(event) => setEditForm((current) => ({ ...current, reason: event.target.value }))}
            />
          </label>
          <button type="submit" disabled={isUpdating || !editForm.resourceId || !editForm.title.trim()}>
            {isUpdating ? "Saving..." : "Save block correction"}
          </button>
        </form>
      )}
    </div>
  );
}

function ResourceDetailView({
  calendarItemsByResourceId = new Map(),
  loading,
  onBack,
  onOpenResourceDetail,
  onPrimeReservation,
  onUpdateResource,
  resourceDetail,
  resources = [],
  scheduleDate,
  scheduleDays = [],
  setScheduleDate,
  updatingResourceId
}) {
  const resource = resourceDetail?.resource;
  const hierarchy = resourceDetail?.hierarchy || {};
  const detailResourceById = useMemo(() => buildResourceMap(resources), [resources]);
  const detailChildrenByParentId = useMemo(() => buildChildrenByParentId(resources), [resources]);
  const children = useMemo(() => (
    detailChildrenByParentId.get(resource?.resourceId) || hierarchy.children || []
  ), [detailChildrenByParentId, hierarchy.children, resource?.resourceId]);
  const descendants = useMemo(() => (
    collectDescendantResources(resource, detailChildrenByParentId)
  ), [detailChildrenByParentId, resource]);
  const summary = hierarchy.summary || {};
  const resourceLevel = resource ? resourceHierarchyLevel(resource, detailResourceById) : "zone";
  const childLevel = resource ? childHierarchyLabel(resource, detailResourceById) : "range";
  const isUpdating = resource && updatingResourceId === resource.resourceId;
  const [inventoryEditOpen, setInventoryEditOpen] = useState(false);
  const [inventoryDraft, setInventoryDraft] = useState(() => ({
    ...inventoryDraftFromResource(resource),
    hierarchyLevel: resourceLevel
  }));

  useEffect(() => {
    setInventoryEditOpen(false);
    setInventoryDraft({
      ...inventoryDraftFromResource(resource),
      hierarchyLevel: resourceLevel
    });
  }, [resource, resourceLevel]);

  const descendantIds = useMemo(() => new Set(descendants.map((item) => item.resourceId)), [descendants]);
  const parentResourceOptions = resources.filter((item) => (
    item.resourceId !== resource?.resourceId &&
    !descendantIds.has(item.resourceId) &&
    resourceHierarchyLevel(item, detailResourceById) === (inventoryDraft.hierarchyLevel === "lane" ? "range" : "zone")
  ));
  const detailCalendarRows = useMemo(() => (
    [resource, ...children].filter(Boolean)
  ), [children, resource]);
  const detailCalendarItemCount = useMemo(() => (
    detailCalendarRows.reduce((total, item) => {
      const byDay = calendarItemsByResourceId.get(item.resourceId);
      if (!byDay) {
        return total;
      }
      return total + scheduleDays.reduce((dayTotal, day) => {
        const bucket = byDay.get(day.key);
        return dayTotal + (bucket?.blocks?.length || 0) + (bucket?.reservations?.length || 0);
      }, 0);
    }, 0)
  ), [calendarItemsByResourceId, detailCalendarRows, scheduleDays]);

  function setInventoryDraftLevel(level) {
    const nextParentOptions = resources.filter((item) => (
      item.resourceId !== resource?.resourceId &&
      !descendantIds.has(item.resourceId) &&
      resourceHierarchyLevel(item, detailResourceById) === (level === "lane" ? "range" : "zone")
    ));
    setInventoryDraft((current) => ({
      ...current,
      hierarchyLevel: level,
      type: resourceTypeForHierarchyLevel(level, current.type),
      parentResourceId: level === "zone" ? "" : nextParentOptions[0]?.resourceId || ""
    }));
  }

  async function submitInventoryRecord(event) {
    event.preventDefault();
    if (!resource || !inventoryDraft.name.trim()) {
      return;
    }
    await onUpdateResource(resource.resourceId, inventoryPatchFromDraft(inventoryDraft));
    setInventoryEditOpen(false);
  }

  function cancelInventoryRecordEdit() {
    setInventoryDraft(inventoryDraftFromResource(resource));
    setInventoryEditOpen(false);
  }

  function moveDetailCalendar(days) {
    if (!setScheduleDate) {
      return;
    }
    const nextDate = new Date(`${scheduleDate || toDateInput(new Date())}T00:00:00`);
    nextDate.setDate(nextDate.getDate() + days);
    setScheduleDate(toDateInput(nextDate));
  }

  if (loading) {
    return <div className="empty-state">Loading facility detail...</div>;
  }

  if (!resource) {
    return (
      <div className="empty-state">
        Facility detail was not found.
        <button type="button" className="secondary-button" onClick={onBack}>Back to facilities</button>
      </div>
    );
  }

  const isZone = resourceLevel === "zone";
  const isRange = resourceLevel === "range";
  const statusHistory = resource.statusHistory || [];
  const resourceNotes = getResourceNotes(resource);
  const childCountLabel = children.length === 1
    ? `1 ${hierarchyLevelLabel(childLevel).toLowerCase()}`
    : pluralizeHierarchyLevel(childLevel, children.length).toLowerCase();
  const reservableChildren = children.filter((child) => child.reservable).length;
  const openChildren = children.filter((child) => (child.status || "open") === "open").length;
  const detailHighlights = [
    {
      label: isZone ? "Ranges inside" : isRange ? "Lanes inside" : "Located inside",
      value: children.length > 0 ? childCountLabel : hierarchy.parent?.name || "Top level",
      detail: children.length > 0
        ? `${openChildren} open / ${reservableChildren} reservable`
        : hierarchy.parent ? hierarchyLevelLabel(resourceHierarchyLevel(hierarchy.parent, detailResourceById)) : "Top-level facility"
    },
    {
      label: "Physical capacity",
      value: formatResourceCapacity(resource, children) || "Not set",
      detail: isZone ? "Rollup from direct ranges" : isRange ? "Range capacity" : "Lane capacity"
    },
    ...(hasResourceDistance(resource) ? [{
      label: "Distance",
      value: formatDistance(resource),
      detail: "Physical distance"
    }] : []),
    {
      label: "Environment",
      value: statusLabel(resource.attributes?.environment || "outdoor"),
      detail: resource.attributes?.assetClass || hierarchyLevelLabel(resourceLevel)
    }
  ].slice(0, 4);

  return (
    <div className="membership-settings-panel resource-detail-view">
      <div className="panel-header resource-detail-hero">
        <div className="resource-detail-title-block">
          <span className="resource-detail-kicker">{hierarchyLevelLabel(resourceLevel)}</span>
          <h3>{resource.name}</h3>
          <p>{resourceNotes || `${hierarchyLevelLabel(resourceLevel)} in the Liberty facility map.`}</p>
          <div className="resource-detail-badges" aria-label="Facility state">
            <span className={`member-status status-${resource.status || "open"}`}>{statusLabel(resource.status || "open")}</span>
            <span className={`app-state ${resource.targetControl?.status === "firing_allowed" ? "is-enabled" : ""}`}>
              {statusLabel(resource.targetControl?.status || "firing_allowed")}
            </span>
            <span className={`app-state ${resource.reservable ? "is-enabled" : ""}`}>
              {resource.reservable ? "Reservable" : "Not reservable"}
            </span>
          </div>
        </div>
        <div className="resource-detail-hero-actions">
          <button type="button" className="secondary-button" onClick={onBack}>Back</button>
          {!inventoryEditOpen && (
            <button type="button" className="primary-action" onClick={() => setInventoryEditOpen(true)}>
              Edit facility
            </button>
          )}
        </div>
      </div>

      <div className="resource-physical-overview" aria-label="Physical facility overview">
        {detailHighlights.map((item) => (
          <div key={item.label}>
            <span>{item.label}</span>
            <strong>{item.value}</strong>
            <small>{item.detail}</small>
          </div>
        ))}
      </div>

      {!inventoryEditOpen && (
        <div className="member-detail-section resource-detail-card resource-detail-calendar-card">
          <div className="panel-header resource-inventory-profile-header">
            <div>
              <h4>Facility calendar</h4>
              <p>Scoped to this {hierarchyLevelLabel(resourceLevel).toLowerCase()} and the facilities directly inside it.</p>
            </div>
            <div className="resource-detail-calendar-actions">
              {detailCalendarItemCount > 0 && (
                <span className="app-state is-enabled">
                  {detailCalendarItemCount} scheduled
                </span>
              )}
              <div className="resource-detail-calendar-controls" aria-label="Facility calendar week">
                <button type="button" className="secondary-button" onClick={() => moveDetailCalendar(-7)}>Previous</button>
                <input
                  aria-label="Facility calendar date"
                  type="date"
                  value={scheduleDate || ""}
                  onChange={(event) => setScheduleDate?.(event.target.value)}
                />
                <button type="button" className="secondary-button" onClick={() => setScheduleDate?.(toDateInput(new Date()))}>Today</button>
                <button type="button" className="secondary-button" onClick={() => moveDetailCalendar(7)}>Next</button>
              </div>
            </div>
          </div>
          <div className="resource-calendar-wrap is-detail">
            <div className="resource-calendar-grid resource-calendar-grid-detail">
              <div className="resource-calendar-heading">Facility</div>
              {scheduleDays.map((day) => (
                <div className="resource-calendar-heading is-day" key={day.key}>
                  <strong>{day.weekday}</strong>
                  <span>{day.label}</span>
                </div>
              ))}
              {detailCalendarRows.map((item) => (
                <ResourceCalendarRow
                  days={scheduleDays}
                  itemsByDay={calendarItemsByResourceId.get(item.resourceId)}
                  key={item.resourceId}
                  onPrimeReservation={onPrimeReservation}
                  resource={item}
                />
              ))}
            </div>
          </div>
          {detailCalendarItemCount === 0 && (
            <div className="resource-detail-calendar-empty">No bookings or holds are scheduled for this week.</div>
          )}
        </div>
      )}

      <div className="member-detail-section resource-detail-card resource-inventory-edit-card">
        {!inventoryEditOpen ? (
          <>
            <div className="panel-header resource-inventory-profile-header">
              <div>
                <h4>Physical profile</h4>
                <p>Permanent facility structure. Reservations and maintenance are managed in their own workspaces.</p>
              </div>
            </div>
            <div className="resource-inventory-profile-grid">
              <div>
                <span>Level</span>
                <strong>{hierarchyLevelLabel(resourceLevel)}</strong>
              </div>
              <div>
                <span>Located inside</span>
                <strong>{hierarchy.parent?.name || "Top level"}</strong>
              </div>
              <div>
                <span>Capacity</span>
                <strong>{formatResourceCapacity(resource, children) || "Not set"}</strong>
              </div>
              {hasResourceDistance(resource) && (
                <div>
                  <span>Distance</span>
                  <strong>{formatDistance(resource)}</strong>
                </div>
              )}
              <div>
                <span>Environment</span>
                <strong>{statusLabel(resource.attributes?.environment || "outdoor")}</strong>
              </div>
              {resource.attributes?.assetClass && (
                <div>
                  <span>Physical category</span>
                  <strong>{resource.attributes.assetClass}</strong>
                </div>
              )}
              {resourceNotes && (
                <div className="resource-inventory-profile-note">
                  <span>Notes</span>
                  <strong>{resourceNotes}</strong>
                </div>
              )}
            </div>
          </>
        ) : (
          <form className="member-form" onSubmit={submitInventoryRecord}>
            <div className="panel-header resource-edit-form-header">
              <div>
                <h4>Edit physical profile</h4>
                <p>Use this for the permanent facility map. Booking windows and maintenance rules live in their own workspaces.</p>
              </div>
              <button type="button" className="secondary-button" onClick={cancelInventoryRecordEdit}>
                Cancel
              </button>
            </div>
          <div className="form-row">
            <label>
              Facility name
              <input
                value={inventoryDraft.name}
                onChange={(event) => setInventoryDraft((current) => ({ ...current, name: event.target.value }))}
                required
              />
            </label>
            <label>
              Level
              <select
                value={inventoryDraft.hierarchyLevel}
                onChange={(event) => setInventoryDraftLevel(event.target.value)}
              >
                <option value="zone">Zone</option>
                <option value="range">Range</option>
                <option value="lane">Lane</option>
              </select>
            </label>
            {inventoryDraft.hierarchyLevel !== "zone" && (
              <label>
                {inventoryDraft.hierarchyLevel === "range" ? "Inside zone" : "Inside range"}
                <select
                  value={inventoryDraft.parentResourceId}
                  onChange={(event) => setInventoryDraft((current) => ({ ...current, parentResourceId: event.target.value }))}
                  required={inventoryDraft.hierarchyLevel === "lane"}
                >
                  <option value="">{inventoryDraft.hierarchyLevel === "range" ? "Top level / no zone yet" : "Select range"}</option>
                  {parentResourceOptions.map((item) => (
                    <option value={item.resourceId} key={item.resourceId}>
                      {resourcePathLabel(item, detailResourceById)}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          <div className="form-row">
            <label>
              Shooter capacity
              <input
                type="number"
                min="0"
                value={inventoryDraft.shooterCapacity}
                onChange={(event) => setInventoryDraft((current) => ({ ...current, shooterCapacity: event.target.value }))}
              />
            </label>
            <label>
              Lanes
              <input
                type="number"
                min="0"
                value={inventoryDraft.laneCapacity}
                onChange={(event) => setInventoryDraft((current) => ({ ...current, laneCapacity: event.target.value }))}
              />
            </label>
            <label>
              Environment
              <select
                value={inventoryDraft.environment}
                onChange={(event) => setInventoryDraft((current) => ({ ...current, environment: event.target.value }))}
              >
                <option value="outdoor">Outdoor</option>
                <option value="indoor">Indoor</option>
                <option value="mixed">Mixed</option>
                <option value="portable">Portable</option>
              </select>
            </label>
          </div>
          <div className="form-row">
            <label>
              Min distance
              <input
                type="number"
                min="0"
                value={inventoryDraft.distanceMin}
                onChange={(event) => setInventoryDraft((current) => ({ ...current, distanceMin: event.target.value }))}
              />
            </label>
            <label>
              Max distance
              <input
                type="number"
                min="0"
                value={inventoryDraft.distanceMax}
                onChange={(event) => setInventoryDraft((current) => ({ ...current, distanceMax: event.target.value }))}
              />
            </label>
            <label>
              Fixed distances
              <input
                value={inventoryDraft.fixedDistances}
                onChange={(event) => setInventoryDraft((current) => ({ ...current, fixedDistances: event.target.value }))}
                placeholder="25, 50, 100"
              />
            </label>
            <label>
              Physical category
              <input
                value={inventoryDraft.assetClass}
                onChange={(event) => setInventoryDraft((current) => ({ ...current, assetClass: event.target.value }))}
                placeholder="Long range, pistol, shotgun, classroom"
              />
            </label>
          </div>
          <label>
            Notes
            <textarea
              rows={3}
              value={inventoryDraft.notes}
              onChange={(event) => setInventoryDraft((current) => ({ ...current, notes: event.target.value }))}
              placeholder="Permanent facility notes for staff"
            />
          </label>
          <button type="submit" disabled={isUpdating || !inventoryDraft.name.trim()}>
            {isUpdating ? "Saving..." : "Save physical profile"}
          </button>
        </form>
        )}
      </div>

      {!inventoryEditOpen && (
        <>
          <div className="member-detail-section resource-detail-card resource-children-card">
            <div className="panel-header resource-inventory-profile-header">
              <div>
                <h4>{children.length > 0 ? `${pluralizeHierarchyLevel(childLevel, children.length)} inside` : "Hierarchy"}</h4>
                <p>{children.length > 0 ? `${childCountLabel} connected to this ${hierarchyLevelLabel(resourceLevel).toLowerCase()}.` : "This facility has no lower-level facilities."}</p>
              </div>
            </div>
            {children.length === 0 && (
              <dl>
                <div>
                  <dt>Located inside</dt>
                  <dd>{hierarchy.parent?.name || "Top level"}</dd>
                </div>
                <div>
                  <dt>Peers at this level</dt>
                  <dd>{summary.siblingCount || 0}</dd>
                </div>
                <div>
                  <dt>Cease-fire checks below</dt>
                  <dd>{summary.targetCheckChildren || 0}</dd>
                </div>
              </dl>
            )}
            {children.length > 0 ? (
              <div className="resource-child-list">
                {children.map((child) => {
                  const childLevelValue = resourceHierarchyLevel(child, detailResourceById);
                  const childChildren = detailChildrenByParentId.get(child.resourceId) || [];
                  const childFacts = [
                    hierarchyLevelLabel(childLevelValue),
                    statusLabel(child.status || "open"),
                    child.reservable ? "Reservable" : "Not reservable",
                    formatResourceCapacity(child, childChildren),
                    hasResourceDistance(child) ? formatDistance(child) : ""
                  ].filter(Boolean);
                  return (
                    <button type="button" className="resource-child-row" key={child.resourceId} onClick={() => onOpenResourceDetail?.(child.resourceId)}>
                      <span>
                        <strong>{child.name}</strong>
                        <small>{childFacts.join(" / ")}</small>
                      </span>
                      {childChildren.length > 0 && <b>{childChildren.length}</b>}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">No lower-level facilities.</div>
            )}
          </div>

          {statusHistory.length > 0 && (
            <div className="member-detail-section resource-detail-card">
              <h4>Status history</h4>
              <div className="member-detail-list">
                {statusHistory.map((item) => (
                  <div key={item.historyId}>
                    <strong>
                      {statusLabel(item.from?.status)} to {statusLabel(item.to?.status)}
                    </strong>
                    <span>
                      {statusLabel(item.from?.targetControl?.status)} to {statusLabel(item.to?.targetControl?.status)}
                      {" / "}
                      {formatDateTime(item.changedAt)}
                    </span>
                    {item.to?.targetControl?.note && <span>{item.to.targetControl.note}</span>}
                    {item.actorUserId && <span>Updated by team member</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ResourceRow({ child = false, childResources = [], descendantCount = 0, depth = 0, hierarchyLevel, onOpenResourceDetail, resource }) {
  const status = resource.status || "open";
  const level = hierarchyLevel || (child ? "range" : "zone");
  const childUnit = level === "zone" ? "range" : "lane";
  const capacity = formatResourceCapacity(resource, child ? [] : childResources, childUnit);
  const facts = [hierarchyLevelLabel(level)];
  if (hasResourceDistance(resource)) {
    facts.push(formatDistance(resource));
  }
  if (capacity) {
    facts.push(capacity);
  }
  if (descendantCount > childResources.length) {
    facts.push(`${descendantCount} total below`);
  }
  const notes = child ? [] : [
    getResourceNotes(resource)
  ].filter(Boolean);

  return (
    <div className={`resource-row ${child ? "is-child" : ""} hierarchy-depth-${depth}`}>
      <div className="resource-row-primary">
        <div className="resource-row-title">
          <strong>{resource.name}</strong>
        </div>
        <div className="resource-row-facts">
          {facts.map((fact) => (
            <span key={fact}>{fact}</span>
          ))}
        </div>
        <div className="resource-row-notes">
          {notes.map((note) => (
            <span key={note}>{note}</span>
          ))}
        </div>
      </div>
      <div className="resource-row-controls">
        <div className="resource-state" aria-label="Facility state">
          <span className={`member-status status-${status}`}>{statusLabel(status)}</span>
          <span className={`app-state ${resource.reservable ? "is-enabled" : ""}`}>
            {resource.reservable ? "Reservable" : "Not reservable"}
          </span>
        </div>
        <button type="button" className="secondary-button resource-open-button" onClick={() => onOpenResourceDetail(resource.resourceId)}>
          Open
        </button>
      </div>
    </div>
  );
}
